import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useParams } from "react-router-dom";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import {
  docsFactibilidadParaTipoUso,
} from "../../config/proyectosFactibilidadDocumentos";
import {
  canAccessGestionTab,
  isProyectosFactibilidadRole,
  isProyectosDisenadorRole,
  isProyectosLiquidacionDisenoRole,
  puedeGestionarLiquidacionDiseno,
  puedeGestionarInformeAvanceObra,
} from "../../utils/proyectosRol";
import { isProyectosAdminRole } from "../../utils/proyectosAdmin";
import { documentoRequiereCargaSoloSolicitante } from "../../config/documentosSoloCargaSolicitante";
import CartaRemisoriaTrazabilidadVista from "./CartaRemisoriaTrazabilidadVista";
import RetieRevisionPanel from "./RetieRevisionPanel";
import LiquidacionDisenoPanel from "./LiquidacionDisenoPanel";
import InformeAvanceObraPanel from "./InformeAvanceObraPanel";
import GestionDocumentalPanel from "./GestionDocumentalPanel";
import ExpedienteDocumentalPanel from "./ExpedienteDocumentalPanel";
import RedisennoTrazabilidadVista from "./RedisennoTrazabilidadVista";
import CalculoBurdenPanel from "./CalculoBurdenPanel";

const orderedKeys = [
  "solicitud_servicio",
  "factibilidad_servicio",
  "revision_aprobacion",
  "ejecucion_obras",
  "puesta_servicio",
];

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

/** Evita enviar claves extra (p. ej. checklist plano) en PATCH que el servidor podría mapear a columnas SQL. */
const FACTIBILIDAD_PATCH_KEYS = [
  "flujo_estado",
  "tipo_tramite",
  "numero_upm",
  "nodo_referencia",
  "nodo_conexion",
  "capacidad_kva",
  "proceso_estado",
  "decision",
  "decision_observacion",
  "observaciones",
  "updated_by",
] as const;

function mergeFactibilidadPayload(form: any, extra: Record<string, any> = {}) {
  const out: Record<string, any> = {};
  for (const key of FACTIBILIDAD_PATCH_KEYS) {
    const v = Object.prototype.hasOwnProperty.call(extra, key) ? extra[key] : form?.[key];
    if (v !== undefined) out[key] = v;
  }
  return out;
}

const REVISION_EJECUCION_PATCH_KEYS = ["proceso_estado", "observaciones", "checklist_json", "updated_by"] as const;

function mergeRevisionPayload(form: any, extra: Record<string, any> = {}) {
  const out: Record<string, any> = {};
  for (const key of REVISION_EJECUCION_PATCH_KEYS) {
    const v = Object.prototype.hasOwnProperty.call(extra, key) ? extra[key] : form?.[key];
    if (v !== undefined) out[key] = v;
  }
  return out;
}

function mergeEjecucionPayload(form: any, extra: Record<string, any> = {}) {
  const out: Record<string, any> = {};
  for (const key of REVISION_EJECUCION_PATCH_KEYS) {
    const v = Object.prototype.hasOwnProperty.call(extra, key) ? extra[key] : form?.[key];
    if (v !== undefined) out[key] = v;
  }
  return out;
}

/** Tras aprobar diseño: activa flujo de liquidación (carta remisoria solo tras cargar liquidación). */
function mergeLiquidacionDisenoTrasDisenoAprobado(checklist: Record<string, any>): Record<string, any> | null {
  const prev = checklist?.liquidacion_diseno_workflow || {};
  const st = String(prev.estado || "").trim();
  if (st === "enviada" || st === "pendiente_carga") return null;
  const hist = Array.isArray(prev.historial) ? [...prev.historial] : [];
  hist.push({
    fecha: new Date().toISOString(),
    accion: "pendiente_carga",
    detalle:
      "Diseño aprobado: pendiente de cargar y enviar la liquidación de diseño al solicitante.",
  });
  return {
    ...prev,
    estado: "pendiente_carga",
    diseno_aprobado_at: new Date().toISOString(),
    historial: hist,
  };
}

const labelMap: Record<string, string> = {
  codigo: "Codigo",
  nombre: "Nombre del proyecto",
  tipo_proyecto: "Tipo de proyecto",
  nivel_tension: "Nivel de tension",
  fecha_estimada_operacion: "Fecha estimada de operacion",
  numero_usuarios: "Numero de usuarios",
  carga_total_solicitada_kva: "Carga total solicitada (kVA)",
  tipo_subestacion: "Tipo de subestacion",
  nodo_referencia: "Nodo de referencia",
  nombre_solicitante: "Nombre del solicitante",
  tipo_documento: "Tipo de documento",
  documento_solicitante: "Documento",
  tipo_persona: "Tipo de persona",
  tipo_persona_juridica: "Tipo de persona juridica",
  correo_notificacion: "Correo",
  telefono_solicitante: "Celular",
  telefono_fijo: "Telefono fijo",
  predio_departamento: "Departamento del predio",
  predio_municipio: "Municipio del predio",
  predio_comuna: "Comuna del predio",
  direccion_predio: "Direccion del predio",
  observaciones: "Observaciones",
  responsable_emcali: "Responsable EMCALI",
  fecha_radicado: "Fecha de solicitud",
  tipo_uso: "Tipo de uso",
  estrato: "Estrato socioeconomico",
  red_cercana: "Red electrica cercana al predio",
  distancia_red: "Distancia a red (mts)",
  nodo_mas_cercano: "Nodo mas cercano",
  nodo_adicional: "Nodo adicional",
};

const sectionMap: Array<{ title: string; keys: string[] }> = [
  {
    title: "Datos basicos del proyecto",
    keys: ["codigo", "nombre", "tipo_proyecto", "nivel_tension", "nodo_referencia", "fecha_radicado"],
  },
  {
    title: "Datos del solicitante",
    keys: [
      "nombre_solicitante",
      "tipo_documento",
      "documento_solicitante",
      "tipo_persona",
      "tipo_persona_juridica",
      "correo_notificacion",
      "telefono_solicitante",
      "telefono_fijo",
    ],
  },
  {
    title: "Ubicacion del predio",
    keys: [
      "predio_departamento",
      "predio_municipio",
      "predio_comuna",
      "direccion_predio",
      "latitud",
      "longitud",
      "magna_este",
      "magna_norte",
    ],
  },
  {
    title: "Uso y actividad economica",
    keys: ["tipo_uso", "estrato"],
  },
  {
    title: "Infraestructura electrica",
    keys: ["red_cercana", "distancia_red", "nodo_mas_cercano", "nodo_adicional"],
  },
  {
    title: "Observaciones y control",
    keys: ["responsable_emcali", "observaciones"],
  },
];

const puestaMetricas = [
  "revision_documentos",
  "visita_previa",
  "aviso_macromedicion",
  "nuevos_servicios",
  "devolucion",
  "correccion",
  "maniobra_ejecucion",
  "entrega_equipos_contratista",
  "cuadrilla",
  "instalacion",
  "enviar_soportes",
  "seguimiento",
  "medidor_legalizado",
  "facturado",
];

const documentosCatalogoBase: Array<{ key: string; label: string; etapaKey: string; grupo: string }> = [
  { key: "solicitud_documento_identidad", label: "Documento de identidad del solicitante", etapaKey: "solicitud_servicio", grupo: "Solicitud del servicio" },
  { key: "solicitud_acreditacion_uso_especial", label: "Documento que acredita uso especial", etapaKey: "solicitud_servicio", grupo: "Solicitud del servicio" },
  { key: "solicitud_formato_creg_e1", label: "Formato solicitud servicio publico CREG E1", etapaKey: "solicitud_servicio", grupo: "Solicitud del servicio" },
  { key: "factibilidad_oficio_factibilidad", label: "Oficio de factibilidad", etapaKey: "solicitud_servicio", grupo: "Solicitud del servicio" },
  { key: "factibilidad_soportes_adicionales", label: "Soportes adicionales factibilidad", etapaKey: "solicitud_servicio", grupo: "Solicitud del servicio" },
  { key: "revision_doc_comprobante_pago_diseno", label: "Comprobante de pago diseño", etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { key: "revision_doc_memorias_calculo", label: "Memorias de calculo", etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { key: "revision_doc_acta_levantada_terreno", label: "Acta levantada en terreno (opcional)", etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { key: "revision_doc_solicitud_grandes_clientes", label: "Solicitud de grandes clientes", etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { key: "revision_doc_declaracion_cumplimiento_deudas", label: "Declaración de cumplimiento de deudas", etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { key: "revision_doc_validacion_provisional", label: "Documento de validación provisional", etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { key: "ejecucion_doc_carta_cambio_constructor", label: "Carta de autorizacion para cambio de constructor (si aplica)", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_matricula_constructor", label: "Matricula profesional del constructor", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_habeas_data_constructor", label: "Formato habeas data del constructor", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_solicitud_supervisor_obra", label: "Solicitud de supervisor de obra", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_informe_primera_visita", label: "Informe de primera visita firmado", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_factura_liquidacion", label: "Factura de liquidacion de trabajos", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_soporte_pago", label: "Soporte de pago de factura", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_acta_visita", label: "Acta de visita de avance de obra", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_documentacion_transformador", label: "Documentacion de transformador (factura, protocolos, garantias)", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_registro_fotografico", label: "Registro fotografico nodo/transformador", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
  { key: "ejecucion_doc_orden_movilizacion", label: "Orden de movilizacion aprobada", etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
];

const documentosAutoGenerados = new Set<string>(["solicitud_formato_creg_e1"]);
const etapasDocumentales = [
  { etapaKey: "solicitud_servicio", grupo: "Solicitud del servicio" },
  { etapaKey: "revision_aprobacion", grupo: "Supervicion y aprobacion" },
  { etapaKey: "ejecucion_obras", grupo: "Ejecucion de obras" },
] as const;

const disenoColumnas = ["item", "descripcion_proceso"] as const;

const disenoLabels: Record<(typeof disenoColumnas)[number], string> = {
  item: "ITEM",
  descripcion_proceso: "Observacion del item",
};

const disenoMatrizInicial: Array<Record<(typeof disenoColumnas)[number], string>> = [
  {
    item: "1",
    descripcion_proceso: "",
  },
];

const disenoMatrizFilaVacia = (): Record<(typeof disenoColumnas)[number], string> => ({
  item: "",
  descripcion_proceso: "",
});

const TIPO_DOC_PLANO_DISENO = "factibilidad_plano_diseno";
const TIPO_DOC_PRESUPUESTO_DISENO = "factibilidad_presupuesto_diseno";
const TIPO_DOC_LIQUIDACION_DISENO = "factibilidad_liquidacion_diseno";
type DisenoDocDevueltoTipo = "plano" | "presupuesto";
type DisenoDevolucionModo = "" | "plano" | "presupuesto" | "ambos";

function documentosDevueltosDesdeModo(modo: DisenoDevolucionModo): DisenoDocDevueltoTipo[] {
  if (modo === "plano") return ["plano"];
  if (modo === "presupuesto") return ["presupuesto"];
  if (modo === "ambos") return ["plano", "presupuesto"];
  return [];
}

function CampoArchivoRevision({
  inputId,
  tipoDocumento,
  ariaLabel,
  accept = ".pdf,.jpg,.jpeg,.png,.awd",
  onArchivo,
}: {
  inputId: string;
  tipoDocumento: string;
  ariaLabel: string;
  accept?: string;
  onArchivo: (file: File | null) => void;
}) {
  return (
    <input
      id={inputId}
      name={tipoDocumento}
      type="file"
      accept={accept}
      aria-label={ariaLabel}
      onChange={(e) => onArchivo(e.target.files?.[0] || null)}
    />
  );
}

/** No exigen carga obligatoria ni bloquean aprobación de supervisión (flujos aparte u opcionales). */
const DOCS_SUPERVISION_OPCIONALES = new Set([
  "revision_doc_acta_levantada_terreno",
  "revision_doc_comprobante_pago_diseno",
  "revision_doc_solicitud_grandes_clientes",
  "revision_doc_declaracion_cumplimiento_deudas",
  "revision_doc_validacion_provisional",
]);

const snapshotDocRevision = (doc: any, fallbackNombre: string) => ({
  id: Number(doc?.id) || null,
  nombre: String(doc?.nombre_archivo || doc?.tipo_documento || fallbackNombre),
  fecha: String(doc?.created_at || new Date().toISOString()),
});

const disenoMatrizNormalizada = (rows: any): Array<Record<(typeof disenoColumnas)[number], string>> => {
  if (!Array.isArray(rows) || rows.length === 0) return [disenoMatrizFilaVacia()];
  return rows.map((row: any, idx: number) => ({
    item: String(idx + 1),
    descripcion_proceso: String(row?.descripcion_proceso || ""),
  }));
};

type DisenoRevision = {
  fecha: string;
  decision: "Aprobado" | "Devuelto";
  observacion: string;
  matriz: Array<Record<(typeof disenoColumnas)[number], string>>;
  /** Solo cuando decision = Devuelto: qué documentos debe corregir el solicitante */
  documentos_devueltos?: DisenoDocDevueltoTipo[];
  plano?: {
    id: number | null;
    nombre: string;
    fecha: string;
  };
  presupuesto?: {
    id: number | null;
    nombre: string;
    fecha: string;
  };
};

type WorkflowRevision = DisenoRevision;

const disenoTrazabilidadNormalizada = (
  revisiones: any,
): DisenoRevision[] => {
  if (!Array.isArray(revisiones)) return [];
  return revisiones.map((rev: any) => {
    const decision = rev?.decision === "Devuelto" ? "Devuelto" : "Aprobado";
    const documentos_devueltos: DisenoDocDevueltoTipo[] = Array.isArray(rev?.documentos_devueltos)
      ? rev.documentos_devueltos.filter((d: string) => d === "plano" || d === "presupuesto")
      : decision === "Devuelto"
        ? ["plano"]
        : [];
    return {
      fecha: String(rev?.fecha || new Date().toISOString()),
      decision,
      observacion: String(rev?.observacion || ""),
      matriz: disenoMatrizNormalizada(rev?.matriz),
      documentos_devueltos,
      plano: rev?.plano
        ? {
            id: Number(rev?.plano?.id) || null,
            nombre: String(rev?.plano?.nombre || TIPO_DOC_PLANO_DISENO),
            fecha: String(rev?.plano?.fecha || rev?.fecha || new Date().toISOString()),
          }
        : undefined,
      presupuesto: rev?.presupuesto
        ? {
            id: Number(rev?.presupuesto?.id) || null,
            nombre: String(rev?.presupuesto?.nombre || TIPO_DOC_PRESUPUESTO_DISENO),
            fecha: String(rev?.presupuesto?.fecha || rev?.fecha || new Date().toISOString()),
          }
        : undefined,
    };
  });
};

const workflowTrazabilidadNormalizada = (revisiones: any): WorkflowRevision[] =>
  disenoTrazabilidadNormalizada(revisiones);

export default function GestionProyectoDetalle() {
  const { id } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [detalle, setDetalle] = React.useState<any>(null);
  const [tab, setTab] = React.useState(0);
  const gestionTabsVisibles = React.useMemo(
    () =>
      [
        { idx: 0, label: "Solicitud del servicio" },
        { idx: 1, label: "Gestión documental" },
        { idx: 2, label: "Diseño" },
        { idx: 3, label: "Supervicion y aprobacion" },
        { idx: 4, label: "Ejecucion de obras" },
        { idx: 5, label: "Puesta en servicio" },
      ].filter((t) => canAccessGestionTab(t.idx)),
    [],
  );
  React.useEffect(() => {
    if (!gestionTabsVisibles.some((t) => t.idx === tab)) {
      setTab(gestionTabsVisibles[0]?.idx ?? 0);
    }
  }, [gestionTabsVisibles, tab]);
  const [solicitudSubtab, setSolicitudSubtab] = React.useState(0);
  const [guardando, setGuardando] = React.useState<string | null>(null);
  const [okMsg, setOkMsg] = React.useState<string | null>(null);
  const [etapasForm, setEtapasForm] = React.useState<any[]>([]);
  const [factibilidadForm, setFactibilidadForm] = React.useState<any>({});
  const [revisionForm, setRevisionForm] = React.useState<any>({});
  const [ejecucionForm, setEjecucionForm] = React.useState<any>({});
  const [puestaForm, setPuestaForm] = React.useState<any>({});
  const [puestaSubtab, setPuestaSubtab] = React.useState(0);
  const [solicitudRevisionObs, setSolicitudRevisionObs] = React.useState("");
  const [mapView, setMapView] = React.useState<"osm" | "google-map" | "google-satellite">("google-map");
  const [camposCorreccion, setCamposCorreccion] = React.useState<string[]>([]);
  const [archivosSeleccionados, setArchivosSeleccionados] = React.useState<Record<string, File | null>>({});
  const [docModalOpen, setDocModalOpen] = React.useState(false);
  const [docModalTipo, setDocModalTipo] = React.useState<string>("");
  const [docModalUrl, setDocModalUrl] = React.useState<string>("");
  const [docModalEstado, setDocModalEstado] = React.useState<"Aprobado" | "Desaprobado">("Aprobado");
  const [docModalObservacion, setDocModalObservacion] = React.useState("");
  const [docModalSoloLectura, setDocModalSoloLectura] = React.useState(false);
  const [factibilidadDecision, setFactibilidadDecision] = React.useState<"Aprobado" | "Devuelto">("Aprobado");
  const [factibilidadResena, setFactibilidadResena] = React.useState("");
  const [gestionDocumentalObs, setGestionDocumentalObs] = React.useState("");
  const [factibilidadDocValidations, setFactibilidadDocValidations] = React.useState<
    Record<string, { estado?: string; observacion?: string; documento_id?: number }>
  >({});
  const [disenoMatriz, setDisenoMatriz] = React.useState<Array<Record<(typeof disenoColumnas)[number], string>>>(disenoMatrizInicial);
  const [disenoDecision, setDisenoDecision] = React.useState<"Aprobado" | "Devuelto">("Aprobado");
  const [disenoObservacion, setDisenoObservacion] = React.useState("");
  const [disenoDevolucionModo, setDisenoDevolucionModo] = React.useState<DisenoDevolucionModo>("");
  const [disenoRevisiones, setDisenoRevisiones] = React.useState<DisenoRevision[]>([]);
  const [revisionExpandida, setRevisionExpandida] = React.useState<number | false>(false);
  const [supervisionMatriz, setSupervisionMatriz] = React.useState<
    Array<Record<(typeof disenoColumnas)[number], string>>
  >([disenoMatrizFilaVacia()]);
  const [supervisionDecision, setSupervisionDecision] = React.useState<"Aprobado" | "Devuelto">("Aprobado");
  const [supervisionObservacion, setSupervisionObservacion] = React.useState("");
  const [supervisionRevisiones, setSupervisionRevisiones] = React.useState<WorkflowRevision[]>([]);
  const [supervisionExpandida, setSupervisionExpandida] = React.useState<number | false>(false);
  const [disenoSubTab, setDisenoSubTab] = React.useState(0);
  const [supervisionSubTab, setSupervisionSubTab] = React.useState(0);
  const [gcDecisionSolicitud, setGcDecisionSolicitud] = React.useState<"Aprobado" | "Devuelto">("Aprobado");
  const [gcObsSolicitud, setGcObsSolicitud] = React.useState("");
  const [gcObsProvisional, setGcObsProvisional] = React.useState("");
  const [cartaRemisoriaVistaTab, setCartaRemisoriaVistaTab] = React.useState(0);
  const [redisennoMotivo, setRedisennoMotivo] = React.useState("");
  const [redisennoModalOpen, setRedisennoModalOpen] = React.useState(false);
  const [redisennoVistaTab, setRedisennoVistaTab] = React.useState(0);
  const [ejecucionMatriz, setEjecucionMatriz] = React.useState<
    Array<Record<(typeof disenoColumnas)[number], string>>
  >([disenoMatrizFilaVacia()]);
  const [ejecucionDecision, setEjecucionDecision] = React.useState<"Aprobado" | "Devuelto">("Aprobado");
  const [ejecucionObservacion, setEjecucionObservacion] = React.useState("");
  const [ejecucionRevisiones, setEjecucionRevisiones] = React.useState<WorkflowRevision[]>([]);
  const [ejecucionExpandida, setEjecucionExpandida] = React.useState<number | false>(false);

  const docsSupervisionObligatorios = React.useMemo(
    () =>
      documentosCatalogoBase.filter(
        (d) => d.etapaKey === "revision_aprobacion" && !DOCS_SUPERVISION_OPCIONALES.has(d.key),
      ),
    [],
  );

  const documentosCatalogo = React.useMemo(() => {
    const seen = new Set<string>();
    const seenLabel = new Set<string>();
    return documentosCatalogoBase.filter((doc) => {
      const labelNorm = doc.label.trim().toLowerCase();
      if (seen.has(doc.key) || seenLabel.has(labelNorm)) return false;
      seen.add(doc.key);
      seenLabel.add(labelNorm);
      return true;
    });
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${getProyectosApiBaseUrl()}/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (!response.ok) throw new Error("No se pudo cargar la gestion del proyecto.");
      const data = await response.json();
      let solicitudFormSnapshot: Record<string, any> = {};
      try {
        const etapaSolicitud = (data?.etapas || []).find(
          (e: any) => e?.etapa_key === "solicitud_servicio",
        );
        const parsed = etapaSolicitud?.observaciones ? JSON.parse(etapaSolicitud.observaciones) : {};
        if (parsed?.formulario && typeof parsed.formulario === "object") {
          solicitudFormSnapshot = parsed.formulario;
        } else if (parsed && typeof parsed === "object") {
          solicitudFormSnapshot = parsed;
        }
      } catch {
        solicitudFormSnapshot = {};
      }
      setDetalle(data);
      setEtapasForm(data?.etapas || []);
      const fbBase = mergeFactibilidadPayload({}, data?.factibilidad || {});
      setFactibilidadForm({
        ...fbBase,
        proceso_estado: fbBase.proceso_estado || data?.factibilidad?.proceso_estado || "Por revisar",
        numero_upm:
          fbBase.numero_upm ||
          solicitudFormSnapshot?.codigo ||
          data?.proyecto?.codigo ||
          "",
      });
      setRevisionForm(mergeRevisionPayload({}, data?.revision_aprobacion || {}));
      setEjecucionForm(mergeEjecucionPayload({}, data?.ejecucion_obras || {}));
      setPuestaForm({
        ...(data?.puesta_servicio || {}),
        tipo_solicitud:
          data?.puesta_servicio?.tipo_solicitud ||
          solicitudFormSnapshot?.tipo_proyecto ||
          data?.proyecto?.tipo_proyecto ||
          "",
        nombre:
          data?.puesta_servicio?.nombre ||
          data?.proyecto?.nombre ||
          solicitudFormSnapshot?.nombre ||
          "",
      });
      const etapaFactibilidad = (data?.etapas || []).find((e: any) => e?.etapa_key === "factibilidad_servicio");
      let payloadFactibilidad: any = {};
      try {
        payloadFactibilidad = etapaFactibilidad?.observaciones
          ? JSON.parse(etapaFactibilidad.observaciones)
          : {};
      } catch {
        payloadFactibilidad = {};
      }
      const decisionInicial =
        payloadFactibilidad?.decision === "Devuelto" ||
        String(data?.factibilidad?.decision || "").toLowerCase().includes("dev")
          ? "Devuelto"
          : "Aprobado";
      setFactibilidadDecision(decisionInicial);
      setFactibilidadResena(
        String(
          payloadFactibilidad?.resena ||
            data?.factibilidad?.observaciones ||
            "",
        ),
      );
      setGestionDocumentalObs(String(payloadFactibilidad?.resena || ""));
      setDisenoMatriz(disenoMatrizNormalizada(payloadFactibilidad?.diseno?.matriz));
      setDisenoDecision(
        payloadFactibilidad?.diseno?.decision === "Devuelto" ? "Devuelto" : "Aprobado",
      );
      setDisenoObservacion(String(payloadFactibilidad?.diseno?.observacion || ""));
      setDisenoRevisiones(disenoTrazabilidadNormalizada(payloadFactibilidad?.diseno?.trazabilidad));
      const dv =
        payloadFactibilidad?.doc_validations && typeof payloadFactibilidad.doc_validations === "object"
          ? payloadFactibilidad.doc_validations
          : {};
      setFactibilidadDocValidations(dv);
    } catch (err: any) {
      setError(err.message || "Error cargando gestión.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const etapasOrdenadas = React.useMemo(() => {
    const etapas = etapasForm || [];
    return [...etapas].sort(
      (a: any, b: any) =>
        orderedKeys.indexOf(a.etapa_key) - orderedKeys.indexOf(b.etapa_key),
    );
  }, [etapasForm]);

  const solicitudEtapa = React.useMemo(
    () => etapasOrdenadas.find((etapa: any) => etapa.etapa_key === "solicitud_servicio") || null,
    [etapasOrdenadas],
  );

  const solicitudPayload = React.useMemo(() => {
    const raw = solicitudEtapa?.observaciones;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [solicitudEtapa]);

  const solicitudFormulario = React.useMemo(() => {
    if (!solicitudPayload) return null;
    if (solicitudPayload?.formulario && typeof solicitudPayload.formulario === "object") {
      return solicitudPayload.formulario;
    }
    if (typeof solicitudPayload === "object") {
      return solicitudPayload;
    }
    return null;
  }, [solicitudPayload]);

  const solicitudYaAprobada = React.useMemo(() => {
    const decisionPayload = String(solicitudPayload?.decision || "").trim().toLowerCase();
    const decisionProyecto = String(detalle?.proyecto?.solicitud_decision || "").trim().toLowerCase();
    return decisionPayload === "aprobado" || decisionProyecto === "aprobado";
  }, [detalle?.proyecto?.solicitud_decision, solicitudPayload?.decision]);

  const factibilidadEtapaPayload = React.useMemo(() => {
    const etapa = (etapasForm || []).find((e: any) => e?.etapa_key === "factibilidad_servicio");
    try {
      return etapa?.observaciones ? JSON.parse(etapa.observaciones) : {};
    } catch {
      return {};
    }
  }, [etapasForm]);

  const gestionDocumentalHabilitada = Boolean(factibilidadEtapaPayload?.gestion_documental_habilitada);
  const gestionDocumentalAprobada = Boolean(factibilidadEtapaPayload?.gestion_documental_aprobada);
  const adminPuedeVerDisenoAntesGestion = isProyectosAdminRole();
  const disenoSubtabAccesible = gestionDocumentalAprobada || adminPuedeVerDisenoAntesGestion;
  const disenoAvisoAdminSinGestion = !gestionDocumentalAprobada && adminPuedeVerDisenoAntesGestion;

  const fichaSolicitud = React.useMemo(() => {
    const base = solicitudFormulario || {};
    return {
      ...base,
      codigo: base.codigo || detalle?.proyecto?.codigo || "",
      nombre: base.nombre || detalle?.proyecto?.nombre || "",
      tipo_proyecto: base.tipo_proyecto || detalle?.proyecto?.tipo_proyecto || "",
      nivel_tension: base.nivel_tension || detalle?.proyecto?.nivel_tension || "",
      responsable_emcali: base.responsable_emcali || detalle?.proyecto?.responsable_emcali || "",
      fecha_radicado: base.fecha_radicado || detalle?.proyecto?.fecha_radicado || "",
    };
  }, [detalle?.proyecto, solicitudFormulario]);

  const codigoSolicitud = React.useMemo(
    () => String((fichaSolicitud as any)?.codigo || detalle?.proyecto?.codigo || "").trim(),
    [detalle?.proyecto?.codigo, fichaSolicitud],
  );

  React.useEffect(() => {
    if (!codigoSolicitud) return;
    setFactibilidadForm((prev: any) => ({
      ...(prev || {}),
      numero_upm: codigoSolicitud,
    }));
  }, [codigoSolicitud]);

  const coordenadasPredio = React.useMemo(() => {
    const lat = Number((fichaSolicitud as any)?.latitud);
    const lng = Number((fichaSolicitud as any)?.longitud);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  }, [fichaSolicitud]);

  React.useEffect(() => {
    const obs = solicitudPayload?.revision_observaciones || "";
    setSolicitudRevisionObs(obs);
    const fields = Array.isArray(solicitudPayload?.editable_fields)
      ? solicitudPayload.editable_fields.filter((f: any) => typeof f === "string")
      : [];
    setCamposCorreccion(fields);
  }, [solicitudPayload]);

  const avanceGeneral = React.useMemo(() => {
    if (!etapasOrdenadas.length) return 0;
    const sum = etapasOrdenadas.reduce(
      (acc: number, etapa: any) => acc + Number(etapa.porcentaje || 0),
      0,
    );
    return Math.round(sum / etapasOrdenadas.length);
  }, [etapasOrdenadas]);

  const avancePuesta = React.useMemo(() => {
    const values = puestaMetricas.map((k) => Number(puestaForm?.[k] ?? 0)).filter((v) => Number.isFinite(v));
    if (!values.length) return Number(puestaForm?.avance_porcentaje || 0);
    const avg = values.reduce((acc, v) => acc + Math.max(0, Math.min(100, v)), 0) / values.length;
    return Math.round(avg);
  }, [puestaForm]);

  const documentosRevisionPorTipo = React.useMemo(() => {
    const map: Record<string, any> = {};
    (detalle?.documentos || []).forEach((doc: any) => {
      const tipo = String(doc?.tipo_documento || "");
      if (!tipo) return;
      if (!map[tipo] || String(doc?.created_at || "") >= String(map[tipo]?.created_at || "")) {
        map[tipo] = doc;
      }
    });
    return map;
  }, [detalle?.documentos]);

  const ultimaRevisionDiseno = React.useMemo(() => {
    if (!disenoRevisiones.length) return null;
    return disenoRevisiones[disenoRevisiones.length - 1];
  }, [disenoRevisiones]);

  const docDisenoPendienteTrasDevolucion = React.useCallback(
    (tipo: DisenoDocDevueltoTipo) => {
      if (!ultimaRevisionDiseno || ultimaRevisionDiseno.decision !== "Devuelto") return false;
      const devueltos = ultimaRevisionDiseno.documentos_devueltos || [];
      if (!devueltos.includes(tipo)) return false;
      const tipoDoc = tipo === "plano" ? TIPO_DOC_PLANO_DISENO : TIPO_DOC_PRESUPUESTO_DISENO;
      const doc = documentosRevisionPorTipo[tipoDoc];
      const fechaRevision = new Date(ultimaRevisionDiseno.fecha);
      if (Number.isNaN(fechaRevision.getTime())) return true;
      if (!doc?.created_at) return true;
      const docAt = new Date(doc.created_at);
      if (Number.isNaN(docAt.getTime())) return true;
      return docAt.getTime() <= fechaRevision.getTime();
    },
    [ultimaRevisionDiseno, documentosRevisionPorTipo],
  );

  const disenoPendienteNuevoCargue = React.useMemo(() => {
    if (!ultimaRevisionDiseno || ultimaRevisionDiseno.decision !== "Devuelto") return false;
    const devueltos = ultimaRevisionDiseno.documentos_devueltos || [];
    if (!devueltos.length) return docDisenoPendienteTrasDevolucion("plano");
    return devueltos.some((t) => docDisenoPendienteTrasDevolucion(t));
  }, [ultimaRevisionDiseno, docDisenoPendienteTrasDevolucion]);

  const disenoPendienteLabels = React.useMemo(() => {
    if (!ultimaRevisionDiseno || ultimaRevisionDiseno.decision !== "Devuelto") return "";
    const devueltos = ultimaRevisionDiseno.documentos_devueltos || [];
    const pendientes = devueltos.filter((t) => docDisenoPendienteTrasDevolucion(t));
    if (!pendientes.length) return "";
    return pendientes.map((t) => (t === "plano" ? "plano" : "presupuesto")).join(" y ");
  }, [ultimaRevisionDiseno, docDisenoPendienteTrasDevolucion]);

  const revisionChecklist = React.useMemo(() => {
    const raw = revisionForm?.checklist_json;
    if (!raw) return {};
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }, [revisionForm]);

  const redisennoWorkflow = React.useMemo(() => {
    const w = revisionChecklist?.redisenno_workflow;
    if (!w || typeof w !== "object") return {} as Record<string, any>;
    return w;
  }, [revisionChecklist]);

  const redisennoEstado = String(redisennoWorkflow?.estado || "ninguno").trim();

  const cartaRemisoriaJefe = React.useMemo(() => {
    const c = revisionChecklist?.carta_remisoria_jefe;
    return c && typeof c === "object" ? c : ({} as Record<string, any>);
  }, [revisionChecklist]);

  const liquidacionDisenoWorkflow = React.useMemo(() => {
    const w = revisionChecklist?.liquidacion_diseno_workflow;
    return w && typeof w === "object" ? w : {};
  }, [revisionChecklist]);

  const liquidacionDisenoEstado = String(liquidacionDisenoWorkflow?.estado || "").trim();

  const cartaRemisoriaEstado = String(cartaRemisoriaJefe?.estado || "").trim();
  const interventorAsignacion = React.useMemo(() => {
    const ia = revisionChecklist?.interventor_asignacion;
    return ia && typeof ia === "object" ? ia : ({} as Record<string, any>);
  }, [revisionChecklist]);
  const retieWorkflow = React.useMemo(() => {
    const rw = revisionChecklist?.retie_workflow;
    return rw && typeof rw === "object" ? rw : ({} as Record<string, any>);
  }, [revisionChecklist]);
  const interventorAsignado = String(interventorAsignacion?.estado || "").trim() === "asignado";
  const mostrarTabRetie =
    interventorAsignado || String(retieWorkflow?.estado || "").trim() !== "";
  const grandesClientesWorkflow = React.useMemo(() => {
    const g = revisionChecklist?.grandes_clientes_workflow;
    return g && typeof g === "object" ? g : ({} as Record<string, any>);
  }, [revisionChecklist]);
  const gcEstado = String(grandesClientesWorkflow?.estado || "sin_solicitud").trim();
  const gcRequiredDocs = React.useMemo(
    () =>
      Array.isArray(grandesClientesWorkflow?.required_doc_types)
        ? grandesClientesWorkflow.required_doc_types.map((t: any) => String(t || ""))
        : [],
    [grandesClientesWorkflow],
  );
  const gcDocsCompletos = React.useMemo(
    () => gcRequiredDocs.every((tipo: string) => !!documentosRevisionPorTipo[tipo]?.id),
    [gcRequiredDocs, documentosRevisionPorTipo],
  );

  const disenoBloqueadoPorAprobacion = React.useMemo(() => {
    if (redisennoEstado === "pendiente_admin") return true;
    if (redisennoEstado === "aprobado") return false;
    if (!ultimaRevisionDiseno || ultimaRevisionDiseno.decision !== "Aprobado") return false;
    const revFecha = ultimaRevisionDiseno?.fecha ? new Date(ultimaRevisionDiseno.fecha).getTime() : 0;
    const planoDoc = documentosRevisionPorTipo[TIPO_DOC_PLANO_DISENO];
    const presDoc = documentosRevisionPorTipo[TIPO_DOC_PRESUPUESTO_DISENO];
    const planoNuevo =
      planoDoc?.created_at && revFecha > 0 && new Date(planoDoc.created_at).getTime() > revFecha;
    const presNuevo =
      presDoc?.created_at && revFecha > 0 && new Date(presDoc.created_at).getTime() > revFecha;
    if (planoNuevo || presNuevo) return false;
    return true;
  }, [redisennoEstado, ultimaRevisionDiseno, documentosRevisionPorTipo]);

  const informeAvanceObraWorkflow = React.useMemo(() => {
    const w = revisionChecklist?.informe_avance_obra_workflow;
    return w && typeof w === "object" ? w : {};
  }, [revisionChecklist]);

  const informesAvanceObraExpediente = React.useMemo(() => {
    const list = Array.isArray(informeAvanceObraWorkflow?.informes) ? informeAvanceObraWorkflow.informes : [];
    return list
      .filter((inf: any) => inf?.tipo_documento)
      .map((inf: any) => ({
        tipo: String(inf.tipo_documento),
        label: String(inf.titulo || "Informe avance de obra"),
      }));
  }, [informeAvanceObraWorkflow]);

  const liquidacionSubtabAccesible =
    isProyectosAdminRole() ||
    disenoBloqueadoPorAprobacion ||
    liquidacionDisenoEstado === "pendiente_carga" ||
    liquidacionDisenoEstado === "enviada";

  const DISENO_SUBTAB_LIQUIDACION = 2;

  React.useEffect(() => {
    if (!liquidacionSubtabAccesible && disenoSubTab === DISENO_SUBTAB_LIQUIDACION) {
      setDisenoSubTab(disenoSubtabAccesible ? 1 : 0);
    }
  }, [liquidacionSubtabAccesible, disenoSubTab, disenoSubtabAccesible]);

  React.useEffect(() => {
    if (isProyectosLiquidacionDisenoRole() && liquidacionSubtabAccesible) {
      setDisenoSubTab(DISENO_SUBTAB_LIQUIDACION);
    }
  }, [liquidacionSubtabAccesible]);

  const resolverGestionDocumental = async (decision: "Aprobado" | "Devuelto") => {
    if (decision === "Devuelto" && !gestionDocumentalObs.trim()) {
      setError("Las observaciones generales son obligatorias al devolver la gestión documental.");
      return;
    }
    await patchData(
      `/${id}/gestion-documental`,
      { decision, observaciones: gestionDocumentalObs.trim() },
      decision === "Aprobado"
        ? "Gestión documental aprobada. Puede asignar diseñador."
        : "Gestión documental devuelta al solicitante.",
    );
    if (decision === "Aprobado") {
      setDisenoSubTab(1);
    }
    await load();
  };

  const ejecucionChecklist = React.useMemo(() => {
    const raw = ejecucionForm?.checklist_json;
    if (!raw) return {};
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }, [ejecucionForm]);

  React.useEffect(() => {
    const data = revisionChecklist?.supervision_workflow || {};
    setSupervisionMatriz(disenoMatrizNormalizada(data?.matriz_actual));
    setSupervisionRevisiones(workflowTrazabilidadNormalizada(data?.trazabilidad));
  }, [revisionChecklist]);

  React.useEffect(() => {
    const data = ejecucionChecklist?.ejecucion_workflow || {};
    setEjecucionMatriz(disenoMatrizNormalizada(data?.matriz_actual));
    setEjecucionRevisiones(workflowTrazabilidadNormalizada(data?.trazabilidad));
  }, [ejecucionChecklist]);

  const supervisionSubtabGrandesClientes = mostrarTabRetie ? 2 : 1;
  const supervisionSubtabInformeAvance = mostrarTabRetie ? 3 : 2;

  React.useEffect(() => {
    const maxSubTab = supervisionSubtabInformeAvance;
    if (supervisionSubTab > maxSubTab) setSupervisionSubTab(maxSubTab);
  }, [mostrarTabRetie, supervisionSubTab, supervisionSubtabInformeAvance]);

  const getChecklistForDoc = React.useCallback(
    (docKey: string) => {
      if (docKey.startsWith("solicitud_documento") || docKey.startsWith("solicitud_acreditacion") || docKey.startsWith("gestion_doc_")) {
        const v = factibilidadDocValidations[docKey];
        if (!v) return {};
        return { [`doc_validation__${docKey}`]: v };
      }
      if (docKey.startsWith("ejecucion_doc_")) return ejecucionChecklist;
      return revisionChecklist;
    },
    [ejecucionChecklist, revisionChecklist, factibilidadDocValidations],
  );

  const docsFactibilidadRevision = React.useMemo(
    () => docsFactibilidadParaTipoUso((fichaSolicitud as any)?.tipo_uso),
    [fichaSolicitud],
  );

  const getDocSuffix = React.useCallback((docKey: string) => {
    if (docKey.startsWith("revision_doc_")) return docKey.replace("revision_doc_", "");
    if (docKey.startsWith("ejecucion_doc_")) return docKey.replace("ejecucion_doc_", "");
    return docKey;
  }, []);

  const bloquearCargaInternaDoc = React.useCallback(
    (tipo: string) => {
      if (isProyectosAdminRole()) return false;
      if (
        tipo === "revision_doc_solicitud_grandes_clientes" &&
        (gcEstado === "sin_solicitud" ||
          gcEstado === "" ||
          gcEstado === "solicitud_recargada_pendiente_revision")
      ) {
        return false;
      }
      return documentoRequiereCargaSoloSolicitante(tipo);
    },
    [gcEstado],
  );

  const getValidacionRevisionDoc = React.useCallback(
    (docKey: string) => {
      if (/^retie_/.test(docKey)) {
        const rwVal = revisionChecklist?.retie_workflow?.validaciones || {};
        return rwVal[docKey] || revisionChecklist[`doc_validation__${docKey}`] || null;
      }
      const suffix = getDocSuffix(docKey);
      return revisionChecklist[`doc_validation__${suffix}`] || null;
    },
    [revisionChecklist, getDocSuffix],
  );

  const getEstadoDocumentoExpediente = React.useCallback(
    (tipo: string): { estado: string; observacion?: string } => {
      const doc = documentosRevisionPorTipo[tipo];
      if (
        tipo.startsWith("gestion_doc_") ||
        tipo.startsWith("solicitud_") ||
        tipo.startsWith("factibilidad_")
      ) {
        const v = factibilidadDocValidations[tipo];
        if (!doc?.id) return { estado: "Sin cargar", observacion: v?.observacion };
        return {
          estado: String(v?.estado || "Pendiente"),
          observacion: v?.observacion,
        };
      }
      if (/^retie_/.test(tipo)) {
        const v = revisionChecklist?.retie_workflow?.validaciones?.[tipo];
        return {
          estado: String(v?.estado || (doc?.id ? "Pendiente" : "Sin cargar")),
          observacion: v?.observacion,
        };
      }
      if (tipo.startsWith("ejecucion_doc_")) {
        const suffix = getDocSuffix(tipo);
        const v = ejecucionChecklist[`doc_validation__${suffix}`];
        return {
          estado: String(v?.estado || (doc?.id ? "Cargado" : "Sin cargar")),
          observacion: v?.observacion,
        };
      }
      const val = getValidacionRevisionDoc(tipo);
      return {
        estado: String(val?.estado || (doc?.id ? "Pendiente" : "Sin cargar")),
        observacion: val?.observacion,
      };
    },
    [
      documentosRevisionPorTipo,
      factibilidadDocValidations,
      revisionChecklist,
      ejecucionChecklist,
      getValidacionRevisionDoc,
      getDocSuffix,
    ],
  );

  const retieTiposExpediente = React.useMemo(() => {
    const fromWorkflow = Array.isArray(revisionChecklist?.retie_workflow?.tipos_requeridos)
      ? revisionChecklist.retie_workflow.tipos_requeridos.map((t: string) => String(t))
      : [];
    const fromDocs = Object.keys(documentosRevisionPorTipo).filter((k) => /^retie_/.test(k));
    return Array.from(new Set([...fromWorkflow, ...fromDocs])).sort();
  }, [revisionChecklist, documentosRevisionPorTipo]);

  const totalDocumentosExpediente = React.useMemo(
    () => Object.values(documentosRevisionPorTipo).filter((d) => d?.id).length,
    [documentosRevisionPorTipo],
  );

  const validarSupervisionDocumental = React.useCallback(
    (decision: "Aprobado" | "Devuelto"): string | null => {
      for (const docReq of docsSupervisionObligatorios) {
        const doc = documentosRevisionPorTipo[docReq.key];
        if (!doc?.id) {
          return `Debe cargar el documento obligatorio: ${docReq.label}.`;
        }
        const estadoVal = String(getValidacionRevisionDoc(docReq.key)?.estado || "Pendiente");
        if (estadoVal === "Pendiente" || !estadoVal) {
          return `Debe revisar (aprobar o rechazar) en el validador: ${docReq.label}.`;
        }
      }
      const actaKey = "revision_doc_acta_levantada_terreno";
      const actaDoc = documentosRevisionPorTipo[actaKey];
      if (actaDoc?.id) {
        const estadoActa = String(getValidacionRevisionDoc(actaKey)?.estado || "Pendiente");
        if (estadoActa === "Pendiente" || !estadoActa) {
          return "Debe revisar el acta de terreno cargada (validador).";
        }
      }
      const hayRechazados = docsSupervisionObligatorios.some(
        (d) => getValidacionRevisionDoc(d.key)?.estado === "Desaprobado",
      );
      if (decision === "Aprobado" && hayRechazados) {
        return "Hay documentos rechazados. Corrija los archivos o registre la supervisión como devuelta.";
      }
      if (decision === "Devuelto" && !hayRechazados && !supervisionObservacion.trim()) {
        return "Indique la observación general de la devolución o rechace documentos en el validador.";
      }
      return null;
    },
    [
      docsSupervisionObligatorios,
      documentosRevisionPorTipo,
      getValidacionRevisionDoc,
      supervisionObservacion,
    ],
  );

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
        <CircularProgress size={20} />
        <Typography>Cargando gestion...</Typography>
      </Stack>
    );
  }

  const patchData = async (path: string, body: any, okLabel: string): Promise<boolean> => {
    setGuardando(path);
    setOkMsg(null);
    setError(null);
    try {
      const response = await fetch(`${getProyectosApiBaseUrl()}${path}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error("No se pudo guardar la información.");
      if (okLabel) setOkMsg(okLabel);
      await load();
      return true;
    } catch (err: any) {
      setError(err.message || "Error guardando cambios.");
      return false;
    } finally {
      setGuardando(null);
    }
  };

  const resolverSolicitud = async (decision: "Aprobado" | "Devuelto" | "Rechazado") => {
    const obs = solicitudRevisionObs.trim();
    if ((decision === "Devuelto" || decision === "Rechazado") && !obs) {
      setError("Las observaciones de revision son obligatorias para devolver o rechazar la solicitud.");
      return;
    }
    if (decision === "Devuelto" && camposCorreccion.length === 0) {
      setError("Seleccione al menos un campo para habilitar la correccion al solicitante.");
      return;
    }
    const payloadObservaciones = JSON.stringify({
      formulario: solicitudFormulario || {},
      revision_observaciones: solicitudRevisionObs || "",
      editable_fields: decision === "Devuelto" ? camposCorreccion : [],
      decision,
      fecha_revision: new Date().toISOString(),
    });
    await patchData(
      `/${id}/etapa/solicitud_servicio`,
      {
        estado: decision === "Aprobado" ? "Completada" : "En revision",
        porcentaje: decision === "Aprobado" ? 100 : 70,
        observaciones: payloadObservaciones,
        solicitud_decision: decision,
      },
      `Solicitud ${decision.toLowerCase()} correctamente.`,
    );
  };

  const confirmarYResolverSolicitud = async (decision: "Aprobado" | "Devuelto" | "Rechazado") => {
    const mensajes: Record<"Aprobado" | "Devuelto" | "Rechazado", string> = {
      Aprobado: "¿Está seguro de aprobar esta solicitud?",
      Devuelto: "¿Está seguro de devolver esta solicitud con observaciones?",
      Rechazado: "¿Está seguro de rechazar esta solicitud?",
    };
    const confirmado = window.confirm(mensajes[decision]);
    if (!confirmado) return;
    await resolverSolicitud(decision);
  };

  const resolverFactibilidad = async (decision: "Aprobado" | "Devuelto") => {
    const docsReq = docsFactibilidadRevision;
    for (const docReq of docsReq) {
      if (!documentosRevisionPorTipo[docReq.key]?.id) {
        setError(`Falta el documento cargado en la solicitud: ${docReq.label}.`);
        return;
      }
    }
    if (decision === "Aprobado") {
      for (const docReq of docsReq) {
        const val = factibilidadDocValidations[docReq.key];
        if (String(val?.estado || "") !== "Aprobado") {
          setError(`Debe aprobar el documento "${docReq.label}" en el validador antes de continuar.`);
          return;
        }
      }
    }
    if (decision === "Devuelto") {
      if (!factibilidadResena.trim()) {
        setError("Las observaciones generales son obligatorias al devolver documentación.");
        return;
      }
      const desaprobados = docsReq.filter(
        (d) => String(factibilidadDocValidations[d.key]?.estado || "") === "Desaprobado",
      );
      if (!desaprobados.length) {
        setError("Debe desaprobar al menos un documento (con observación) en el validador.");
        return;
      }
      for (const d of desaprobados) {
        if (!String(factibilidadDocValidations[d.key]?.observacion || "").trim()) {
          setError(`Indique la observación al desaprobar: ${d.label}.`);
          return;
        }
      }
    }

    const desaprobadosTipos =
      decision === "Devuelto"
        ? docsReq
            .filter((d) => String(factibilidadDocValidations[d.key]?.estado || "") === "Desaprobado")
            .map((d) => d.key)
        : [];

    const payloadObservaciones = JSON.stringify({
      decision,
      resena: factibilidadResena.trim(),
      doc_validations: factibilidadDocValidations,
      gestion_documental_habilitada: factibilidadEtapaPayload?.gestion_documental_habilitada,
      gestion_documental_aprobada: factibilidadEtapaPayload?.gestion_documental_aprobada,
      public_upload_enabled: decision === "Devuelto" && desaprobadosTipos.length > 0,
      required_doc_types: decision === "Devuelto" ? desaprobadosTipos : [],
      allowed_extensions: ["pdf", "png", "jpg", "jpeg"],
      fecha_revision: new Date().toISOString(),
    });

    await patchData(
      `/${id}/etapa/factibilidad_servicio`,
      {
        estado: decision === "Aprobado" ? "Completada" : "En revision",
        porcentaje: decision === "Aprobado" ? 100 : 30,
        observaciones: payloadObservaciones,
      },
      decision === "Aprobado"
        ? "Documentación de la solicitud aprobada."
        : "Documentación devuelta. El solicitante podrá recargar solo los archivos indicados.",
    );

    await patchData(
      `/${id}/factibilidad`,
      mergeFactibilidadPayload(factibilidadForm, {
        numero_upm: codigoSolicitud || factibilidadForm.numero_upm || "",
        decision,
        proceso_estado: decision === "Aprobado" ? "Aprobado" : "En revision",
        decision_observacion: factibilidadResena.trim(),
        observaciones: factibilidadResena.trim(),
      }),
      "",
    );
    if (decision === "Aprobado") {
      const extraRev: Record<string, any> = { proceso_estado: "En ejecucion" };
      if (disenoDecision === "Aprobado") {
        const liqMerge = mergeLiquidacionDisenoTrasDisenoAprobado(revisionChecklist);
        if (liqMerge) {
          extraRev.checklist_json = { ...revisionChecklist, liquidacion_diseno_workflow: liqMerge };
        }
      }
      await patchData(
        `/${id}/revision-aprobacion`,
        mergeRevisionPayload(revisionForm, extraRev),
        "Revision y aprobacion paso a En ejecucion.",
      );
    }
  };

  const registrarRevisionDiseno = async () => {
    if (disenoBloqueadoPorAprobacion) {
      setError("El diseño ya fue aprobado. Esta sección queda deshabilitada.");
      return;
    }
    if (disenoPendienteNuevoCargue) {
      setError("Debe cargarse un nuevo diseño antes de registrar una nueva revisión.");
      return;
    }
    if (disenoDecision === "Devuelto" && !disenoObservacion.trim()) {
      setError("Agrega una observacion para registrar una devolucion en diseno.");
      return;
    }
    const planoActual = documentosRevisionPorTipo[TIPO_DOC_PLANO_DISENO];
    const presupuestoActual = documentosRevisionPorTipo[TIPO_DOC_PRESUPUESTO_DISENO];
    if (!planoActual?.id) {
      setError("Debe cargar el plano de diseño antes de registrar la revisión.");
      return;
    }
    if (!presupuestoActual?.id) {
      setError("Debe cargar el presupuesto de diseño antes de registrar la revisión.");
      return;
    }
    let documentosDevueltos: DisenoDocDevueltoTipo[] = [];
    if (disenoDecision === "Devuelto") {
      if (!disenoDevolucionModo) {
        setError("Seleccione qué debe devolver el solicitante: plano, presupuesto o ambos.");
        return;
      }
      documentosDevueltos = documentosDevueltosDesdeModo(disenoDevolucionModo);
    }
    const requiredDocTypes =
      disenoDecision === "Devuelto"
        ? documentosDevueltos.map((t) =>
            t === "plano" ? TIPO_DOC_PLANO_DISENO : TIPO_DOC_PRESUPUESTO_DISENO,
          )
        : [];
    const nuevaTrazabilidad = [
      ...disenoRevisiones,
      {
        fecha: new Date().toISOString(),
        decision: disenoDecision,
        observacion: disenoObservacion.trim(),
        matriz: disenoMatrizNormalizada(disenoMatriz),
        documentos_devueltos: disenoDecision === "Devuelto" ? documentosDevueltos : undefined,
        plano: snapshotDocRevision(planoActual, TIPO_DOC_PLANO_DISENO),
        presupuesto: snapshotDocRevision(presupuestoActual, TIPO_DOC_PRESUPUESTO_DISENO),
      },
    ];
    setDisenoRevisiones(nuevaTrazabilidad);
    const payloadObservaciones = JSON.stringify({
      decision: factibilidadDecision,
      resena: factibilidadResena.trim(),
      diseno: {
        decision: disenoDecision,
        observacion: disenoObservacion.trim(),
        documentos_devueltos: documentosDevueltos,
        trazabilidad: nuevaTrazabilidad,
        matriz: disenoMatriz,
      },
      public_upload_enabled: disenoDecision === "Devuelto" && requiredDocTypes.length > 0,
      required_doc_types: requiredDocTypes,
      allowed_extensions: ["pdf", "awd", "png", "jpg", "jpeg", "xls", "xlsx"],
      fecha_revision: new Date().toISOString(),
    });
    const decisionDiseno = disenoDecision;
    const limpiarRedisenno = String(revisionChecklist?.redisenno_workflow?.estado || "").trim() === "aprobado";
    let nextRevisionChecklist: Record<string, any> = { ...revisionChecklist };
    if (limpiarRedisenno) {
      const rw = revisionChecklist?.redisenno_workflow || {};
      const h = Array.isArray(rw.historial) ? [...rw.historial] : [];
      h.push({
        fecha: new Date().toISOString(),
        accion: "archivo_post_aprobacion_diseno",
        detalle:
          "El flujo activo de rediseño finalizó al registrar diseño aprobado; el estado pasó a «ninguno» (historial conservado).",
      });
      nextRevisionChecklist = {
        ...nextRevisionChecklist,
        redisenno_workflow: {
          ...rw,
          estado: "ninguno",
          historial: h,
        },
      };
    }
    let revisionPatchNeeded = limpiarRedisenno;
    if (decisionDiseno === "Aprobado") {
      const liqMerge = mergeLiquidacionDisenoTrasDisenoAprobado(nextRevisionChecklist);
      if (liqMerge) {
        nextRevisionChecklist = { ...nextRevisionChecklist, liquidacion_diseno_workflow: liqMerge };
        revisionPatchNeeded = true;
      }
    }
    await patchData(
      `/${id}/etapa/factibilidad_servicio`,
      {
        estado: "En revision",
        porcentaje: 40,
        observaciones: payloadObservaciones,
      },
      "Revision de diseno registrada en trazabilidad.",
    );
    if (revisionPatchNeeded) {
      await patchData(
        `/${id}/revision-aprobacion`,
        mergeRevisionPayload(revisionForm, {
          checklist_json: nextRevisionChecklist,
        }),
        "Flujo de liquidación de diseño actualizado.",
      );
    }
    if (decisionDiseno === "Aprobado") {
      setDisenoSubTab(DISENO_SUBTAB_LIQUIDACION);
    }
    setDisenoDecision("Aprobado");
    setDisenoObservacion("");
    setDisenoDevolucionModo("");
    setDisenoMatriz([disenoMatrizFilaVacia()]);
  };

  const registrarRevisionSupervision = async () => {
    const errDocs = validarSupervisionDocumental(supervisionDecision);
    if (errDocs) {
      setError(errDocs);
      return;
    }
    if (supervisionDecision === "Devuelto" && !supervisionObservacion.trim()) {
      setError("Agrega una observacion para registrar una devolucion en supervision.");
      return;
    }
    const nuevaTrazabilidad = [
      ...supervisionRevisiones,
      {
        fecha: new Date().toISOString(),
        decision: supervisionDecision,
        observacion: supervisionObservacion.trim(),
        matriz: disenoMatrizNormalizada(supervisionMatriz),
      },
    ];
    const checklistSiguiente: Record<string, any> = {
      ...revisionChecklist,
      supervision_workflow: {
        matriz_actual: disenoMatrizNormalizada(supervisionMatriz),
        trazabilidad: nuevaTrazabilidad,
      },
    };
    if (supervisionDecision === "Aprobado") {
      checklistSiguiente.supervision_correccion = {
        activo: false,
        required_doc_types: [],
        allowed_extensions: ["pdf", "jpg", "jpeg", "png", "awd"],
      };
    }
    await patchData(
      `/${id}/revision-aprobacion`,
      mergeRevisionPayload(revisionForm, {
        proceso_estado: supervisionDecision === "Aprobado" ? "Aprobado" : "En revision",
        checklist_json: checklistSiguiente,
      }),
      "Revision de supervision registrada.",
    );
    if (supervisionDecision === "Aprobado") {
      await patchData(
        `/${id}/ejecucion-obras`,
        mergeEjecucionPayload(ejecucionForm, {
          proceso_estado: "En ejecucion",
        }),
        "Ejecucion de obras paso a En ejecucion.",
      );
    }
    setSupervisionDecision("Aprobado");
    setSupervisionObservacion("");
    setSupervisionMatriz([disenoMatrizFilaVacia()]);
  };

  const registrarSolicitudGrandesClientes = async () => {
    const solicitudDoc = documentosRevisionPorTipo["revision_doc_solicitud_grandes_clientes"];
    if (!solicitudDoc?.id) {
      setError("Debe cargar el documento de solicitud de grandes clientes.");
      return;
    }
    if (gcDecisionSolicitud === "Devuelto" && !gcObsSolicitud.trim()) {
      setError("Debe indicar observación para devolver la solicitud de grandes clientes.");
      return;
    }
    const nextState =
      gcDecisionSolicitud === "Aprobado"
        ? "pendiente_documentos_solicitante"
        : "solicitud_devuelta";
    const reqDocs =
      gcDecisionSolicitud === "Aprobado"
        ? ["revision_doc_certificado_libertad", "revision_doc_declaracion_cumplimiento_deudas"]
        : ["revision_doc_solicitud_grandes_clientes"];
    const ok = await patchData(
      `/${id}/revision-aprobacion`,
      mergeRevisionPayload(revisionForm, {
        checklist_json: {
          ...revisionChecklist,
          grandes_clientes_workflow: {
            ...(grandesClientesWorkflow || {}),
            estado: nextState,
            decision_solicitud: gcDecisionSolicitud,
            observacion_solicitud: gcObsSolicitud.trim(),
            solicitud_documento_id: Number(solicitudDoc?.id || 0),
            required_doc_types: reqDocs,
            allowed_extensions: ["pdf", "jpg", "jpeg", "png", "awd"],
            updated_at: new Date().toISOString(),
          },
        },
      }),
      gcDecisionSolicitud === "Aprobado"
        ? "Solicitud de grandes clientes aprobada. Se notificó al solicitante para cargar documentos."
        : "Solicitud de grandes clientes devuelta.",
    );
    if (ok) {
      setGcObsSolicitud("");
      setGcDecisionSolicitud("Aprobado");
    }
  };

  const registrarAprobacionProvisionalGrandesClientes = async () => {
    if (!gcDocsCompletos) {
      setError("Aún faltan documentos del solicitante (certificado y/o declaración).");
      return;
    }
    const validacionProv = documentosRevisionPorTipo["revision_doc_validacion_provisional"];
    if (!validacionProv?.id) {
      setError("Debe cargar el documento de validación provisional.");
      return;
    }
    await patchData(
      `/${id}/revision-aprobacion`,
      mergeRevisionPayload(revisionForm, {
        checklist_json: {
          ...revisionChecklist,
          grandes_clientes_workflow: {
            ...(grandesClientesWorkflow || {}),
            estado: "aprobado_provisional",
            observacion_provisional: gcObsProvisional.trim(),
            validacion_provisional_documento_id: Number(validacionProv.id || 0),
            updated_at: new Date().toISOString(),
          },
        },
      }),
      "Aprobación provisional de grandes clientes registrada.",
    );
    setGcObsProvisional("");
  };

  const solicitarRedisenno = async () => {
    const motivo = redisennoMotivo.trim();
    if (!motivo) {
      setError("Indique por qué se requiere volver a la fase de diseño (rediseño).");
      return;
    }
    if (redisennoEstado === "pendiente_admin") {
      setError("Ya hay una solicitud de rediseño pendiente de aprobación del administrador.");
      return;
    }
    if (redisennoEstado === "aprobado") {
      setError("El rediseño ya fue autorizado. Suba el nuevo plano en la pestaña Diseño.");
      return;
    }
    const usuarioSes = (() => {
      try {
        const u = localStorage.getItem("user");
        return u ? atob(u) : "sistema";
      } catch {
        return "sistema";
      }
    })();
    const prevRw = revisionChecklist?.redisenno_workflow || {};
    const prevHist = Array.isArray(prevRw.historial) ? [...prevRw.historial] : [];
    const nowSol = new Date().toISOString();
    prevHist.push({
      fecha: nowSol,
      accion: "solicitud_redisenno",
      motivo,
      usuario: usuarioSes,
    });
    const ok = await patchData(
      `/${id}/revision-aprobacion`,
      mergeRevisionPayload(revisionForm, {
        checklist_json: {
          ...revisionChecklist,
          redisenno_workflow: {
            ...prevRw,
            estado: "pendiente_admin",
            motivo,
            solicitado_at: nowSol,
            solicitado_por: usuarioSes,
            resuelto_at: null,
            resuelto_por: null,
            comentario_admin: null,
            historial: prevHist,
          },
        },
      }),
      "Solicitud enviada. El diseño queda bloqueado hasta que un administrador la apruebe en el menú «Aprobaciones rediseño».",
    );
    if (ok) {
      setRedisennoModalOpen(false);
      setRedisennoMotivo("");
    }
  };

  const registrarRevisionEjecucion = async () => {
    if (ejecucionDecision === "Devuelto" && !ejecucionObservacion.trim()) {
      setError("Agrega una observacion para registrar una devolucion en ejecucion de obras.");
      return;
    }
    const nuevaTrazabilidad = [
      ...ejecucionRevisiones,
      {
        fecha: new Date().toISOString(),
        decision: ejecucionDecision,
        observacion: ejecucionObservacion.trim(),
        matriz: disenoMatrizNormalizada(ejecucionMatriz),
      },
    ];
    await patchData(
      `/${id}/ejecucion-obras`,
      mergeEjecucionPayload(ejecucionForm, {
        proceso_estado: ejecucionDecision === "Aprobado" ? "Aprobado" : "En revision",
        checklist_json: {
          ...ejecucionChecklist,
          ejecucion_workflow: {
            matriz_actual: disenoMatrizNormalizada(ejecucionMatriz),
            trazabilidad: nuevaTrazabilidad,
          },
        },
      }),
      "Revision de ejecucion de obras registrada.",
    );
    setEjecucionDecision("Aprobado");
    setEjecucionObservacion("");
    setEjecucionMatriz([disenoMatrizFilaVacia()]);
  };

  const descargarActaPdf = () => {
    const win = window.open("", "_blank", "width=1000,height=800");
    if (!win) return;

    const payloadCompleto = {
      ...(solicitudFormulario || {}),
      ...(detalle?.proyecto || {}),
    } as Record<string, any>;

    const sections = [
      {
        title: "Datos basicos del proyecto",
        columns: 3,
        keys: [
          "codigo",
          "nombre",
          "tipo_proyecto",
          "nivel_tension",
          "nodo_referencia",
          "fecha_radicado",
          "estado_global",
          "solicitud_decision",
          "responsable_emcali",
        ],
      },
      {
        title: "Datos del solicitante",
        columns: 3,
        keys: [
          "nombre_solicitante",
          "solicitante",
          "tipo_documento",
          "documento_solicitante",
          "tipo_persona",
          "tipo_persona_juridica",
          "correo_notificacion",
          "telefono_solicitante",
          "telefono_fijo",
        ],
      },
      {
        title: "Direccion y ubicacion del predio",
        columns: 2,
        keys: [
          "predio_departamento",
          "predio_municipio",
          "predio_comuna",
          "direccion_predio",
          "latitud",
          "longitud",
          "magna_este",
          "magna_norte",
        ],
      },
      {
        title: "Datos tecnicos y complementarios",
        columns: 3,
        keys: [
          "fecha_estimada_operacion",
          "numero_usuarios",
          "carga_total_solicitada_kva",
          "tipo_subestacion",
          "nodo_mas_cercano",
          "nodo_adicional",
          "distancia_red",
          "red_cercana",
          "tipo_uso",
          "estrato",
          "observaciones",
        ],
      },
    ];

    const renderCards = (keys: string[], columns: number) => {
      const width = columns === 3 ? "31.8%" : "48.7%";
      return keys
        .map((key) => {
          const raw = payloadCompleto[key];
          const value =
            raw === null || raw === undefined ? "-" : String(raw).trim() || "-";
          const label = labelMap[key] || key;
          return `<div style="width:${width};min-height:62px;border:1px solid #d1d5db;border-radius:8px;padding:8px;background:#fff;">
            <div style="font-size:11px;color:#6b7280;font-weight:700;margin-bottom:4px;">${label}</div>
            <div style="font-size:13px;color:#111827;word-break:break-word;">${value}</div>
          </div>`;
        })
        .join("");
    };

    const sectionsHtml = sections
      .map(
        (section) => `
        <div style="margin-top:12px;">
          <div style="font-size:14px;font-weight:800;color:#355d12;margin-bottom:8px;border-left:4px solid #518711;padding-left:8px;">
            ${section.title}
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            ${renderCards(section.keys, section.columns)}
          </div>
        </div>`,
      )
      .join("");

    const latPdf = String(payloadCompleto.latitud ?? "").trim();
    const lngPdf = String(payloadCompleto.longitud ?? "").trim();
    const tieneGeo = !!latPdf && !!lngPdf;

    const html = `
      <html>
        <head>
          <title>Acta solicitud - ${String((fichaSolicitud as any)?.codigo || detalle?.proyecto?.codigo || "")}</title>
          <meta charset="utf-8" />
        </head>
        <body style="font-family: Arial, sans-serif; color:#111827; margin:24px;">
          <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #518711;padding-bottom:12px;margin-bottom:16px;">
            <div style="display:flex;align-items:center;gap:12px;">
              <img src="${window.location.origin}/images/logo-emcali.webp" alt="EMCALI" style="height:54px;" />
              <div>
                <h2 style="margin:0;color:#355d12;">Acta de revision de solicitud</h2>
                <div style="font-size:12px;color:#4b5563;">Proyecto clase 2 - Gestion interna EMCALI</div>
              </div>
            </div>
            <div style="font-size:12px;color:#374151;">
              <div><strong>Estado:</strong> ${detalle?.proyecto?.estado_global || "-"}</div>
              <div><strong>Fecha:</strong> ${new Date().toLocaleString()}</div>
            </div>
          </div>
          ${sectionsHtml}
          ${tieneGeo ? `<div style="margin-top:10px;font-size:12px;"><a href="https://www.google.com/maps?q=${latPdf},${lngPdf}" target="_blank" rel="noreferrer">Ver ubicacion en Google Maps</a></div>` : ""}
          <div style="margin-top:16px;">
            <div style="font-weight:700;margin-bottom:6px;">Observaciones de revision</div>
            <div style="border:1px solid #d1d5db;padding:10px;min-height:56px;">${(solicitudRevisionObs || "-").replace(/\n/g, "<br/>")}</div>
          </div>
        </body>
      </html>
    `;

    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const subirDocumentoRevision = async (tipo: string) => {
    const archivo = archivosSeleccionados[tipo];
    if (!archivo) {
      setError("Seleccione un archivo antes de subir.");
      return;
    }
    if (bloquearCargaInternaDoc(tipo)) {
      setError(
        "Este documento solo lo puede cargar el solicitante por el portal público («Corregir datos o cargar documentos»). Un administrador de proyectos puede subirlo aquí si es necesario.",
      );
      return;
    }
    try {
      setGuardando(`doc-upload-${tipo}`);
      const form = new FormData();
      form.append("archivo", archivo);
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/${id}/documentos/revision/${tipo}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: (() => {
            form.append("etapa_key", documentosCatalogo.find((d) => d.key === tipo)?.etapaKey || "revision_aprobacion");
            return form;
          })(),
        },
      );
      if (!response.ok) throw new Error("No se pudo subir el documento.");
      setOkMsg("Documento subido correctamente.");
      setArchivosSeleccionados((prev) => ({ ...prev, [tipo]: null }));
      await load();
    } catch (err: any) {
      setError(err.message || "Error subiendo documento.");
    } finally {
      setGuardando(null);
    }
  };

  const abrirValidadorDocumento = async (tipo: string) => {
    const doc = documentosRevisionPorTipo[tipo];
    if (!doc?.id) return;
    try {
      setGuardando(`doc-open-${tipo}`);
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/documentos/${doc.id}/archivo`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudo abrir el documento.");
      const mimeType = data?.mimeType || "application/pdf";
      const url = `data:${mimeType};base64,${data?.contentBase64 || ""}`;
      const checklist = getChecklistForDoc(tipo);
      const suffix = getDocSuffix(tipo);
      const validacion =
        /^retie_/.test(tipo)
          ? getValidacionRevisionDoc(tipo)
          : checklist?.[`doc_validation__${suffix}`] || {};
      setDocModalTipo(tipo);
      setDocModalUrl(url);
      setDocModalEstado(validacion?.estado === "Desaprobado" ? "Desaprobado" : "Aprobado");
      setDocModalObservacion(String(validacion?.observacion || ""));
      setDocModalSoloLectura(false);
      setDocModalOpen(true);
    } catch (err: any) {
      setError(err.message || "Error abriendo validador.");
    } finally {
      setGuardando(null);
    }
  };

  const abrirDocumento = async (tipo: string) => {
    const doc = documentosRevisionPorTipo[tipo];
    if (!doc?.id) return;
    try {
      setGuardando(`doc-view-${tipo}`);
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/documentos/${doc.id}/archivo`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudo visualizar el documento.");
      const mimeType = data?.mimeType || "application/pdf";
      const url = `data:${mimeType};base64,${data?.contentBase64 || ""}`;
      const checklist = getChecklistForDoc(tipo);
      const suffix = getDocSuffix(tipo);
      const validacion =
        /^retie_/.test(tipo)
          ? getValidacionRevisionDoc(tipo)
          : checklist?.[`doc_validation__${suffix}`] || {};
      setDocModalTipo(tipo);
      setDocModalUrl(url);
      setDocModalEstado(validacion?.estado === "Desaprobado" ? "Desaprobado" : "Aprobado");
      setDocModalObservacion(String(validacion?.observacion || ""));
      setDocModalSoloLectura(true);
      setDocModalOpen(true);
    } catch (err: any) {
      setError(err.message || "Error visualizando documento.");
    } finally {
      setGuardando(null);
    }
  };

  const abrirDocumentoPorId = async (documentoId: number, tipo: string) => {
    if (!documentoId) return;
    try {
      setGuardando(`doc-view-id-${documentoId}`);
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/documentos/${documentoId}/archivo`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudo visualizar el documento.");
      const mimeType = data?.mimeType || "application/pdf";
      const url = `data:${mimeType};base64,${data?.contentBase64 || ""}`;
      setDocModalTipo(tipo);
      setDocModalUrl(url);
      setDocModalEstado("Aprobado");
      setDocModalObservacion("");
      setDocModalSoloLectura(true);
      setDocModalOpen(true);
    } catch (err: any) {
      setError(err.message || "Error visualizando documento histórico.");
    } finally {
      setGuardando(null);
    }
  };

  const descargarDocumento = async (tipo: string) => {
    const doc = documentosRevisionPorTipo[tipo];
    if (!doc?.id) return;
    try {
      setGuardando(`doc-download-${tipo}`);
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/documentos/${doc.id}/archivo`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudo descargar el documento.");
      const mimeType = data?.mimeType || "application/pdf";
      const base64 = data?.contentBase64 || "";
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      const extension = mimeType.includes("pdf")
        ? "pdf"
        : mimeType.includes("png")
          ? "png"
          : mimeType.includes("jpeg") || mimeType.includes("jpg")
            ? "jpg"
            : "bin";
      const fileName = `${doc?.tipo_documento || tipo}.${extension}`;
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
    } catch (err: any) {
      setError(err.message || "Error descargando documento.");
    } finally {
      setGuardando(null);
    }
  };

  const guardarValidacionDocumento = async () => {
    const doc = documentosRevisionPorTipo[docModalTipo];
    if (!doc?.id) return;
    if (docModalEstado === "Desaprobado" && !docModalObservacion.trim()) {
      setError("La observación es obligatoria al desaprobar un documento.");
      return;
    }
    const esDocSupervision = docModalTipo.startsWith("revision_doc_");
    const ok = await patchData(
      `/${id}/documentos/revision/validar`,
      {
        tipo_documento: docModalTipo,
        documento_id: doc.id,
        estado: docModalEstado,
        observacion: docModalObservacion,
      },
      docModalEstado === "Desaprobado" && esDocSupervision
        ? "Documento rechazado. Se notificó por correo al encargado del trámite."
        : `Documento ${docModalEstado.toLowerCase()} correctamente.`,
    );
    if (ok && (docModalTipo.startsWith("solicitud_documento") || docModalTipo.startsWith("solicitud_acreditacion") || docModalTipo.startsWith("gestion_doc_"))) {
      setFactibilidadDocValidations((prev) => ({
        ...prev,
        [docModalTipo]: {
          documento_id: doc.id,
          estado: docModalEstado,
          observacion: docModalObservacion.trim(),
        },
      }));
    }
    setDocModalOpen(false);
  };

  const renderFactibilidadDocumentosRevision = () => (
    <Grid item xs={12}>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Revise únicamente la documentación radicada en la solicitud (cédula del solicitante
        {(fichaSolicitud as any)?.tipo_uso === "Especial" ? " y documento de uso especial" : ""}).
        Apruebe o desapruebe cada archivo. Para avanzar a <strong>diseño</strong>, todos deben estar aprobados.
      </Alert>
      <Grid container spacing={1.2}>
        {docsFactibilidadRevision.map((docReq) => {
          const doc = documentosRevisionPorTipo[docReq.key];
          const checklist = getChecklistForDoc(docReq.key);
          const validacion = checklist?.[`doc_validation__${docReq.key}`] || null;
          const estado = validacion?.estado || (doc?.id ? "Pendiente" : "Sin cargar");
          return (
            <Grid item xs={12} key={docReq.key}>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Typography fontWeight={700}>{docReq.label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cargado en la solicitud del servicio
                    </Typography>
                    {!doc?.id && (
                      <Typography variant="caption" color="error" display="block">
                        El solicitante no cargó este archivo al crear la solicitud.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando !== null}
                        onClick={() => abrirValidadorDocumento(docReq.key)}
                      >
                        Revisar y validar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando !== null}
                        onClick={() => abrirDocumento(docReq.key)}
                      >
                        Visualizar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando !== null}
                        onClick={() => descargarDocumento(docReq.key)}
                      >
                        Descargar
                      </Button>
                      <Chip
                        size="small"
                        label={estado}
                        color={
                          estado === "Aprobado"
                            ? "success"
                            : estado === "Desaprobado"
                              ? "error"
                              : "default"
                        }
                      />
                    </Stack>
                    {!!validacion?.observacion && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Obs: {String(validacion.observacion)}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );

  const renderDocumentosEtapa = (etapaKey: string, grupo: string, opts?: { supervisionRevision?: boolean }) => (
    <Grid item xs={12}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.8 }}>
        {grupo}
      </Typography>
      {opts?.supervisionRevision && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Revise <strong>cada documento cargado</strong> con el botón «Validador»: apruebe o rechace.
          La documentación de conexiones complejas (Ley 1581, cédulas, RUT, planos, etc.) se gestiona en{" "}
          <strong>Gestión documental</strong> y en <strong>Diseño</strong>; aquí solo aplican memorias de cálculo,
          comprobante de pago y documentos propios de supervisión. Si rechaza un archivo, la observación es
          obligatoria. Para aprobar la supervisión, los documentos obligatorios de esta etapa deben estar cargados
          y aprobados.
        </Alert>
      )}
      <Grid container spacing={1.2}>
        {documentosCatalogo.filter((d) => d.etapaKey === etapaKey).map((docReq) => {
          const doc = documentosRevisionPorTipo[docReq.key];
          const esAutoGenerado = documentosAutoGenerados.has(docReq.key);
          const esOpcionalSupervision = DOCS_SUPERVISION_OPCIONALES.has(docReq.key);
          const puedeVisualizar = Boolean(doc?.id) || esAutoGenerado;
          const etapaDocumento = String(doc?.etapa_key || docReq.etapaKey);
          const procesoCorrecto = etapaDocumento === docReq.etapaKey;
          const checklist = getChecklistForDoc(docReq.key);
          const suffix = getDocSuffix(docReq.key);
          const validacion = checklist?.[`doc_validation__${suffix}`] || null;
          const estado = esAutoGenerado
            ? (solicitudYaAprobada ? "Generado" : "Pendiente")
            : !doc?.id && esOpcionalSupervision
              ? "Opcional"
              : !doc?.id
                ? "Sin cargar"
                : validacion?.estado || (opts?.supervisionRevision ? "Pendiente revision" : "Pendiente");
          return (
            <Grid item xs={12} key={`${etapaKey}-${docReq.key}`}>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography fontWeight={700}>{docReq.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tipo: {docReq.key}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Proceso: {docReq.grupo}
                    </Typography>
                    {!procesoCorrecto && (
                      <Typography variant="caption" display="block" color="warning.main">
                        Registrado en proceso distinto ({etapaDocumento}). Debe quedar en {docReq.etapaKey}.
                      </Typography>
                    )}
                    {esAutoGenerado && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Documento autogenerado desde Solicitud del servicio.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {!esAutoGenerado ? (
                      bloquearCargaInternaDoc(docReq.key) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 300 }}>
                          Carga solo por el solicitante (portal público). Como administrador puede subir aquí.
                        </Typography>
                      ) : (
                        <CampoArchivoRevision
                          inputId={`doc-upload-${docReq.key}`}
                          tipoDocumento={docReq.key}
                          ariaLabel={docReq.label}
                          onArchivo={(file) =>
                            setArchivosSeleccionados((prev) => ({
                              ...prev,
                              [docReq.key]: file,
                            }))
                          }
                        />
                      )
                    ) : (
                      <Chip size="small" color="info" label="Generado automático" />
                    )}
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                      {!esAutoGenerado && !bloquearCargaInternaDoc(docReq.key) && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => subirDocumentoRevision(docReq.key)}
                          disabled={guardando !== null}
                        >
                          Subir archivo
                        </Button>
                      )}
                      {!esAutoGenerado && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!doc?.id || guardando !== null}
                          onClick={() => abrirValidadorDocumento(docReq.key)}
                        >
                          Validador
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!puedeVisualizar || guardando !== null}
                        onClick={() => {
                          if (!doc?.id && esAutoGenerado) {
                            descargarActaPdf();
                            return;
                          }
                          abrirDocumento(docReq.key);
                        }}
                      >
                        Visualizar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando !== null}
                        onClick={() => descargarDocumento(docReq.key)}
                      >
                        Descargar
                      </Button>
                      <Chip
                        size="small"
                        label={estado}
                        color={
                          estado === "Aprobado" || estado === "Generado"
                            ? "success"
                            : estado === "Desaprobado"
                              ? "error"
                              : "default"
                        }
                      />
                    </Stack>
                    {!!validacion?.observacion && (
                      <Typography variant="caption" color="text.secondary">
                        Obs: {String(validacion.observacion)}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {okMsg && <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>}
      {detalle?.proyecto && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>{detalle.proyecto.nombre}</Typography>
          <Typography variant="body2" color="text.secondary">
            Codigo: {detalle.proyecto.codigo} | Estado: {detalle.proyecto.estado_global || "-"}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">Avance del flujo</Typography>
              <Typography variant="body2" fontWeight={700}>{avanceGeneral}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={avanceGeneral} />
          </Box>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            {isProyectosFactibilidadRole() && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Su rol solo permite revisar la etapa de <strong>Solicitud del servicio</strong> (solicitud inicial y
                documentación de factibilidad).
              </Alert>
            )}
            {isProyectosDisenadorRole() && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Como diseñador verá las etapas de diseño en adelante. La asignación la realiza el jefe de la unidad.
              </Alert>
            )}
            <Tabs
              value={gestionTabsVisibles.some((t) => t.idx === tab) ? tab : gestionTabsVisibles[0]?.idx ?? 0}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
            >
              {gestionTabsVisibles.map((t) => (
                <Tab key={t.idx} label={t.label} value={t.idx} />
              ))}
            </Tabs>
            <Divider sx={{ my: 2 }} />

            {tab === 0 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    En esta etapa se revisa toda la informacion registrada en la creacion del proyecto para decidir si se aprueba, se devuelve con observaciones o se rechaza.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <Tabs value={solicitudSubtab} onChange={(_, v) => setSolicitudSubtab(v)}>
                    <Tab label="Solicitud inicial" />
                    <Tab label="Factibilidad" />
                  </Tabs>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>
                {solicitudSubtab === 0 && (
                  <>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 0,
                      borderRadius: 2,
                      borderColor: "#9fbf5a",
                      overflow: "hidden",
                      background: "linear-gradient(180deg, #f8fdf1 0%, #ffffff 20%)",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #d7e5bc",
                        backgroundColor: "#eef7df",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <img src="/images/logo-emcali.webp" alt="Logo EMCALI" style={{ height: 42, width: "auto" }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#355d12", lineHeight: 1.1 }}>
                            Acta de revision de solicitud
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#4b5563" }}>
                            Proyecto clase 2 - Gestion interna EMCALI
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        label={detalle?.proyecto?.estado_global || "Sin estado"}
                        sx={{ fontWeight: 700, backgroundColor: "#518711", color: "#fff" }}
                      />
                    </Box>

                    <Box sx={{ p: 1.5 }}>
                      {sectionMap.map((section, index) => (
                        <Accordion key={section.title} disableGutters defaultExpanded={index < 2} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f7faf2" }}>
                            <Typography fontWeight={700} sx={{ color: "#334155" }}>
                              {section.title}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={1}>
                              {section.keys.map((key) => {
                                const texto = String((fichaSolicitud as any)?.[key] ?? "").trim();
                                return (
                                  <Grid key={key} item xs={12} md={6}>
                                    <Paper
                                      variant="outlined"
                                      sx={{ p: 1, borderRadius: 1.5, borderColor: "#e5e7eb", minHeight: 62 }}
                                    >
                                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                                        {labelMap[key] || key}
                                      </Typography>
                                      <Typography variant="body2">{texto || "-"}</Typography>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}

                      <Accordion disableGutters defaultExpanded sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f7faf2" }}>
                          <Typography fontWeight={700} sx={{ color: "#334155" }}>
                            Mapa visual del predio
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={6}>
                              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5, height: "100%" }}>
                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                                  Coordenadas locales (WGS84)
                                </Typography>
                                <Typography variant="body2">
                                  Latitud: {String((fichaSolicitud as any)?.latitud || "-")}
                                </Typography>
                                <Typography variant="body2">
                                  Longitud: {String((fichaSolicitud as any)?.longitud || "-")}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                                  Coordenadas MAGNA SIRGAS
                                </Typography>
                                <Typography variant="body2">
                                  Este: {String((fichaSolicitud as any)?.magna_este || "-")}
                                </Typography>
                                <Typography variant="body2">
                                  Norte: {String((fichaSolicitud as any)?.magna_norte || "-")}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Paper variant="outlined" sx={{ p: 0.5, borderRadius: 1.5 }}>
                                {coordenadasPredio ? (
                                  <Box>
                                    <Stack direction="row" spacing={1} sx={{ p: 1, flexWrap: "wrap" }}>
                                      <Button
                                        size="small"
                                        variant={mapView === "google-map" ? "contained" : "outlined"}
                                        onClick={() => setMapView("google-map")}
                                      >
                                        Google mapa
                                      </Button>
                                      <Button
                                        size="small"
                                        variant={mapView === "google-satellite" ? "contained" : "outlined"}
                                        onClick={() => setMapView("google-satellite")}
                                      >
                                        Google satelite
                                      </Button>
                                      <Button
                                        size="small"
                                        variant={mapView === "osm" ? "contained" : "outlined"}
                                        onClick={() => setMapView("osm")}
                                      >
                                        OpenStreetMap
                                      </Button>
                                    </Stack>
                                    <Box
                                      component="iframe"
                                      title="Mapa del predio"
                                      src={
                                        mapView === "google-map"
                                          ? `https://maps.google.com/maps?q=${coordenadasPredio.lat},${coordenadasPredio.lng}&z=17&output=embed`
                                          : mapView === "google-satellite"
                                            ? `https://maps.google.com/maps?q=${coordenadasPredio.lat},${coordenadasPredio.lng}&z=18&t=k&output=embed`
                                            : `https://www.openstreetmap.org/export/embed.html?bbox=${coordenadasPredio.lng - 0.01}%2C${coordenadasPredio.lat - 0.01}%2C${coordenadasPredio.lng + 0.01}%2C${coordenadasPredio.lat + 0.01}&layer=mapnik&marker=${coordenadasPredio.lat}%2C${coordenadasPredio.lng}`
                                      }
                                      sx={{ width: "100%", height: 260, border: 0, borderRadius: 1 }}
                                    />
                                    <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                                      <Button
                                        size="small"
                                        variant="text"
                                        href={`https://www.google.com/maps?q=${coordenadasPredio.lat},${coordenadasPredio.lng}`}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Abrir en Google Maps
                                      </Button>
                                    </Stack>
                                  </Box>
                                ) : (
                                  <Box sx={{ p: 2 }}>
                                    <Typography color="text.secondary">
                                      No hay coordenadas validas para pintar el mapa del predio.
                                    </Typography>
                                  </Box>
                                )}
                              </Paper>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  {solicitudYaAprobada && (
                    <Alert severity="success" sx={{ mb: 1.5 }}>
                      La solicitud ya fue aprobada. Esta etapa queda bloqueada y no permite nuevas acciones.
                    </Alert>
                  )}
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Campos para correccion del solicitante (cuando se devuelve)
                    </Typography>
                    <Grid container spacing={0.5}>
                      {Object.entries(fichaSolicitud || {}).map(([key, value]) => {
                        const txt = String(value ?? "").trim();
                        if (!txt) return null;
                        const checked = camposCorreccion.includes(key);
                        return (
                          <Grid item xs={12} md={4} key={`corr-${key}`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={checked}
                                  disabled={solicitudYaAprobada}
                                  onChange={(_, isChecked) =>
                                    setCamposCorreccion((prev) =>
                                      isChecked
                                        ? (prev.includes(key) ? prev : [...prev, key])
                                        : prev.filter((k) => k !== key),
                                    )
                                  }
                                />
                              }
                              label={labelMap[key] || key}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Typography variant="caption" color="text.secondary">
                      Portal público (misma entrada que «Corregir datos o cargar documentos» en la sede de trámites):
                      {" "}
                      <strong>{`${window.location.origin}/proyectos/crear/correccion`}</strong>
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observaciones de revision"
                    fullWidth
                    multiline
                    minRows={4}
                    required
                    value={solicitudRevisionObs}
                    disabled={solicitudYaAprobada}
                    onChange={(e) => setSolicitudRevisionObs(e.target.value)}
                    helperText="Obligatorio al devolver o rechazar. Se envia al solicitante por correo con el detalle del motivo."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                    <Button variant="outlined" disabled={guardando !== null} onClick={descargarActaPdf}>
                      Descargar PDF
                    </Button>
                    <Button variant="contained" color="success" disabled={guardando !== null || solicitudYaAprobada} onClick={() => confirmarYResolverSolicitud("Aprobado")}>
                      Aprobar solicitud
                    </Button>
                    <Button variant="contained" color="warning" disabled={guardando !== null || solicitudYaAprobada} onClick={() => confirmarYResolverSolicitud("Devuelto")}>
                      Devolver con observaciones
                    </Button>
                    <Button variant="contained" color="error" disabled={guardando !== null || solicitudYaAprobada} onClick={() => confirmarYResolverSolicitud("Rechazado")}>
                      Rechazar solicitud
                    </Button>
                  </Stack>
                </Grid>
                  </>
                )}
                {solicitudSubtab === 1 && (
                  <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Factibilidad — revisión documental
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Código: {codigoSolicitud || detalle?.proyecto?.codigo || "-"} · Uso:{" "}
                    {(fichaSolicitud as any)?.tipo_uso || "-"}
                  </Typography>
                </Grid>
                {renderFactibilidadDocumentosRevision()}
                <Grid item xs={12}>
                  <TextField
                    label="Observaciones generales (obligatorias al devolver)"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={factibilidadResena}
                    onChange={(e) => setFactibilidadResena(e.target.value)}
                    helperText="Al devolver documentación se envían por correo junto con la observación de cada archivo desaprobado."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={guardando !== null || !solicitudYaAprobada}
                      onClick={() => resolverFactibilidad("Aprobado")}
                    >
                      Aprobar factibilidad (pasa a diseño)
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={guardando !== null || !solicitudYaAprobada}
                      onClick={() => resolverFactibilidad("Devuelto")}
                    >
                      Devolver documentación
                    </Button>
                  </Stack>
                  {!solicitudYaAprobada && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Primero debe aprobar la solicitud del servicio en la pestaña anterior.
                    </Typography>
                  )}
                </Grid>
                  </>
                )}
              </Grid>
            )}

            {tab === -1 && (
              <Grid container spacing={1.2}>
                {etapasDocumentales.map(({ etapaKey, grupo }) => (
                  <Grid item xs={12} key={etapaKey}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.8 }}>
                      {grupo}
                    </Typography>
                    <Grid container spacing={1.2}>
                      {documentosCatalogo.filter((d) => d.etapaKey === etapaKey).map((docReq) => {
                        const doc = documentosRevisionPorTipo[docReq.key];
                        const esAutoGenerado = documentosAutoGenerados.has(docReq.key);
                        const puedeVisualizar = Boolean(doc?.id) || esAutoGenerado;
                        const etapaDocumento = String(doc?.etapa_key || docReq.etapaKey);
                        const procesoCorrecto = etapaDocumento === docReq.etapaKey;
                        const checklist = getChecklistForDoc(docReq.key);
                        const suffix = getDocSuffix(docReq.key);
                        const validacion = checklist?.[`doc_validation__${suffix}`] || null;
                        const estado = esAutoGenerado
                          ? (solicitudYaAprobada ? "Generado" : "Pendiente")
                          : (validacion?.estado || "Pendiente");
                        return (
                          <Grid item xs={12} key={docReq.key}>
                            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} md={4}>
                                  <Typography fontWeight={700}>{docReq.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Tipo: {docReq.key}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    Proceso: {docReq.grupo}
                                  </Typography>
                                  {!procesoCorrecto && (
                                    <Typography variant="caption" display="block" color="warning.main">
                                      Registrado en proceso distinto ({etapaDocumento}). Debe quedar en {docReq.etapaKey}.
                                    </Typography>
                                  )}
                                  {esAutoGenerado && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Documento autogenerado desde Solicitud del servicio.
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  {!esAutoGenerado ? (
                                    bloquearCargaInternaDoc(docReq.key) ? (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 300 }}>
                                        Carga solo por el solicitante (portal público). Como administrador puede subir aquí.
                                      </Typography>
                                    ) : (
                                      <CampoArchivoRevision
                                        inputId={`doc-upload-legacy-${docReq.key}`}
                                        tipoDocumento={docReq.key}
                                        ariaLabel={docReq.label}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onArchivo={(file) =>
                                          setArchivosSeleccionados((prev) => ({
                                            ...prev,
                                            [docReq.key]: file,
                                          }))
                                        }
                                      />
                                    )
                                  ) : (
                                    <Chip size="small" color="info" label="Generado automático" />
                                  )}
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                                    {!esAutoGenerado && !bloquearCargaInternaDoc(docReq.key) && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => subirDocumentoRevision(docReq.key)}
                                        disabled={guardando !== null}
                                      >
                                        Subir archivo
                                      </Button>
                                    )}
                                    {!esAutoGenerado && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!doc?.id || guardando !== null}
                                        onClick={() => abrirValidadorDocumento(docReq.key)}
                                      >
                                        Validador
                                      </Button>
                                    )}
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!puedeVisualizar || guardando !== null}
                                      onClick={() => {
                                        if (!doc?.id && esAutoGenerado) {
                                          descargarActaPdf();
                                          return;
                                        }
                                        abrirDocumento(docReq.key);
                                      }}
                                    >
                                      Visualizar
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!doc?.id || guardando !== null}
                                      onClick={() => descargarDocumento(docReq.key)}
                                    >
                                      Descargar
                                    </Button>
                                    <Chip
                                      size="small"
                                      label={estado}
                                      color={
                                        estado === "Aprobado" || estado === "Generado"
                                          ? "success"
                                          : estado === "Desaprobado"
                                            ? "error"
                                            : "default"
                                      }
                                    />
                                  </Stack>
                                  {!!validacion?.observacion && (
                                    <Typography variant="caption" color="text.secondary">
                                      Obs: {String(validacion.observacion)}
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}

            {tab === 1 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <ExpedienteDocumentalPanel
                    documentosPorTipo={documentosRevisionPorTipo}
                    retieTiposRequeridos={retieTiposExpediente}
                    informesAvanceObra={informesAvanceObraExpediente}
                    getEstadoDocumento={getEstadoDocumentoExpediente}
                    onVisualizar={abrirDocumento}
                    onDescargar={descargarDocumento}
                    guardando={guardando !== null}
                    totalDocumentosCargados={totalDocumentosExpediente}
                  />
                </Grid>
              </Grid>
            )}

            {tab === 2 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Tabs
                    value={disenoSubTab}
                    onChange={(_, v) => setDisenoSubTab(v)}
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                  >
                    <Tab label="Gestión documental" />
                    <Tab label="Diseño" disabled={!disenoSubtabAccesible} />
                    <Tab label="Liquidación" disabled={!liquidacionSubtabAccesible} />
                  </Tabs>
                </Grid>

                {disenoSubTab === 0 && (
                  <Grid item xs={12}>
                    <GestionDocumentalPanel
                      solicitudAprobada={solicitudYaAprobada}
                      gestionHabilitada={gestionDocumentalHabilitada}
                      gestionAprobada={gestionDocumentalAprobada}
                      docValidations={factibilidadDocValidations}
                      documentosPorTipo={documentosRevisionPorTipo}
                      observaciones={gestionDocumentalObs}
                      onObservacionesChange={setGestionDocumentalObs}
                      guardando={guardando !== null}
                      onValidar={abrirValidadorDocumento}
                      onVisualizar={abrirDocumento}
                      onDescargar={descargarDocumento}
                      onResolver={resolverGestionDocumental}
                    />
                  </Grid>
                )}

                {disenoSubTab === 1 && (
                <>
                {(() => {
                  const da = revisionChecklist?.disenador_asignacion;
                  if (!gestionDocumentalAprobada && !adminPuedeVerDisenoAntesGestion) {
                    return (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          Debe completar y aprobar la <strong>gestión documental</strong> antes de continuar con diseño y asignación de diseñador.
                        </Alert>
                      </Grid>
                    );
                  }
                  return (
                    <>
                      {disenoAvisoAdminSinGestion && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            La gestión documental aún no está aprobada. Como administrador tiene acceso completo a
                            esta sección para gestionar el diseño.
                          </Alert>
                        </Grid>
                      )}
                      {!da || String(da.estado) !== "asignado" ? (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            Pendiente de asignación de diseñador por el jefe de la unidad (menú «Asignación de diseñador»).
                          </Alert>
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <Alert severity="success">
                            Diseñador asignado: <strong>{String(da.disenador_nombre || da.disenador_usuario)}</strong>
                            {da.auto_asignado ? " (automático)" : ""}
                          </Alert>
                        </Grid>
                      )}
                    </>
                  );
                })()}
                <Grid item xs={12}>
                  <Alert severity="info">
                    El plano de diseño (AutoCAD) y el presupuesto los carga el solicitante por el portal público
                    tras asignar diseñador, salvo gestión excepcional por administrador.
                  </Alert>
                </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Esta pestaña conserva la trazabilidad de cada revision de diseño, indicando si se aprueba o se devuelve.
                      </Alert>
                    </Grid>
                    {disenoBloqueadoPorAprobacion && redisennoEstado !== "pendiente_admin" && (
                      <Grid item xs={12}>
                        <Alert severity="success">
                          El diseño ya fue aprobado en la última revisión. La sección queda deshabilitada.
                        </Alert>
                      </Grid>
                    )}
                    {redisennoEstado === "pendiente_admin" && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          Solicitud de rediseño enviada: esperando aprobación del administrador en «Aprobaciones rediseño».
                          El diseño queda bloqueado hasta entonces.
                        </Alert>
                      </Grid>
                    )}
                    {redisennoEstado === "aprobado" && (
                      <Grid item xs={12}>
                        <Alert severity="success">
                          Rediseño autorizado por administración: puede cargar nuevo plano y presupuesto y registrar una nueva revisión.
                        </Alert>
                      </Grid>
                    )}
                    {redisennoEstado === "rechazado" && (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          La solicitud de rediseño fue rechazada
                          {redisennoWorkflow.comentario_admin
                            ? `: ${String(redisennoWorkflow.comentario_admin)}`
                            : "."}
                        </Alert>
                      </Grid>
                    )}
                    {!disenoBloqueadoPorAprobacion && disenoPendienteNuevoCargue && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          La última revisión devolvió:{" "}
                          <strong>
                            {(ultimaRevisionDiseno?.documentos_devueltos || ["plano"])
                              .map((t) => (t === "plano" ? "plano" : "presupuesto"))
                              .join(" y ")}
                          </strong>
                          . Falta cargar nuevamente: <strong>{disenoPendienteLabels || "documentación"}</strong>{" "}
                          (portal público o administrador).
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        Cargue <strong>plano</strong> y <strong>presupuesto</strong> antes de registrar la revisión.
                        Si la decisión es <strong>Devuelto</strong>, debe elegir obligatoriamente si devuelve el plano,
                        el presupuesto o ambos; el solicitante solo podrá recargar eso en el portal.
                      </Alert>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Parametros y logica del proceso de diseño
                      </Typography>
                      {(
                        [
                          {
                            tipo: TIPO_DOC_PLANO_DISENO,
                            titulo: "Plano de diseño",
                            subirLabel: "Subir plano",
                            verLabel: "Visualizar plano actual",
                            chipOk: "Plano cargado",
                            chipNo: "Sin plano",
                          },
                          {
                            tipo: TIPO_DOC_PRESUPUESTO_DISENO,
                            titulo: "Presupuesto de diseño",
                            subirLabel: "Subir presupuesto",
                            verLabel: "Visualizar presupuesto actual",
                            chipOk: "Presupuesto cargado",
                            chipNo: "Sin presupuesto",
                          },
                        ] as const
                      ).map((docCfg) => (
                        <Paper key={docCfg.tipo} variant="outlined" sx={{ p: 1.2, borderRadius: 1.5, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8 }}>
                            {docCfg.titulo} para esta revisión
                          </Typography>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={4}>
                              {bloquearCargaInternaDoc(docCfg.tipo) ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 360 }}>
                                  Carga solo por el solicitante (portal público). Como administrador puede subir aquí.
                                </Typography>
                              ) : (
                                <CampoArchivoRevision
                                  inputId={`diseno-upload-${docCfg.tipo}`}
                                  tipoDocumento={docCfg.tipo}
                                  ariaLabel={docCfg.titulo}
                                  accept=".pdf,.awd,.jpg,.jpeg,.png,.xls,.xlsx"
                                  onArchivo={(file) =>
                                    setArchivosSeleccionados((prev) => ({
                                      ...prev,
                                      [docCfg.tipo]: file,
                                    }))
                                  }
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                                {!bloquearCargaInternaDoc(docCfg.tipo) && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => subirDocumentoRevision(docCfg.tipo)}
                                    disabled={guardando !== null || disenoBloqueadoPorAprobacion}
                                  >
                                    {docCfg.subirLabel}
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!documentosRevisionPorTipo[docCfg.tipo]?.id || guardando !== null}
                                  onClick={() => abrirDocumento(docCfg.tipo)}
                                >
                                  {docCfg.verLabel}
                                </Button>
                                <Chip
                                  size="small"
                                  label={
                                    documentosRevisionPorTipo[docCfg.tipo]?.id ? docCfg.chipOk : docCfg.chipNo
                                  }
                                  color={documentosRevisionPorTipo[docCfg.tipo]?.id ? "success" : "default"}
                                />
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Diligencie los campos base del proceso. Se dejan vacios para que el encargado registre la informacion.
                      </Typography>
                      <Grid container spacing={1}>
                        {disenoMatriz.map((fila, idx) => (
                          <Grid item xs={12} key={`fila-diseno-${idx}`}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                              <Grid container spacing={1}>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    label={disenoLabels.item}
                                    fullWidth
                                    size="small"
                                    value={idx + 1}
                                    InputProps={{ readOnly: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={9}>
                                  <TextField
                                    label={disenoLabels.descripcion_proceso}
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    value={fila.descripcion_proceso || ""}
                                    disabled={disenoBloqueadoPorAprobacion}
                                    onChange={(e) =>
                                      setDisenoMatriz((prev) =>
                                        prev.map((f, i) =>
                                          i === idx
                                            ? { ...f, item: String(i + 1), descripcion_proceso: e.target.value }
                                            : { ...f, item: String(i + 1) },
                                        ),
                                      )
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={() =>
                            setDisenoMatriz((prev) => [
                              ...prev.map((f, i) => ({ ...f, item: String(i + 1) })),
                              { ...disenoMatrizFilaVacia(), item: String(prev.length + 1) },
                            ])
                          }
                          disabled={guardando !== null || disenoBloqueadoPorAprobacion}
                        >
                          Agregar fila base
                        </Button>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label="Decision de revision"
                        fullWidth
                        size="small"
                        value={disenoDecision}
                        disabled={disenoBloqueadoPorAprobacion || disenoPendienteNuevoCargue}
                        onChange={(e) => {
                          const v = (e.target.value as "Aprobado" | "Devuelto") || "Aprobado";
                          setDisenoDecision(v);
                          if (v !== "Devuelto") {
                            setDisenoDevolucionModo("");
                          }
                        }}
                      >
                        <MenuItem value="Aprobado">Aprobado</MenuItem>
                        <MenuItem value="Devuelto">Devuelto</MenuItem>
                      </TextField>
                    </Grid>
                    {disenoDecision === "Devuelto" && (
                      <Grid item xs={12} md={8}>
                        <TextField
                          select
                          required
                          label="¿Qué documentos se devuelven al solicitante?"
                          fullWidth
                          size="small"
                          value={disenoDevolucionModo}
                          disabled={disenoBloqueadoPorAprobacion || disenoPendienteNuevoCargue}
                          onChange={(e) =>
                            setDisenoDevolucionModo((e.target.value as DisenoDevolucionModo) || "")
                          }
                          helperText="Obligatorio al devolver. En el portal solo podrá recargar lo que indique aquí."
                          error={disenoDecision === "Devuelto" && !disenoDevolucionModo}
                        >
                          <MenuItem value="">
                            <em>Seleccione plano, presupuesto o ambos</em>
                          </MenuItem>
                          <MenuItem value="plano">Solo plano de diseño</MenuItem>
                          <MenuItem value="presupuesto">Solo presupuesto de diseño</MenuItem>
                          <MenuItem value="ambos">Plano y presupuesto</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                    {disenoDecision === "Devuelto" && disenoDevolucionModo && (
                      <Grid item xs={12}>
                        <Alert severity="warning" sx={{ py: 0.75 }}>
                          El solicitante podrá corregir en el portal únicamente:{" "}
                          <strong>
                            {disenoDevolucionModo === "ambos"
                              ? "plano y presupuesto"
                              : disenoDevolucionModo === "plano"
                                ? "plano de diseño"
                                : "presupuesto de diseño"}
                          </strong>
                          . La decisión quedará en el historial de trazabilidad.
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        label="Observacion de revision de diseño"
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        value={disenoObservacion}
                        disabled={disenoBloqueadoPorAprobacion || disenoPendienteNuevoCargue}
                        onChange={(e) => setDisenoObservacion(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                        <Button
                          variant="contained"
                          onClick={registrarRevisionDiseno}
                          disabled={
                            guardando !== null || disenoBloqueadoPorAprobacion || disenoPendienteNuevoCargue                          }
                        >
                          Registrar revision en trazabilidad
                        </Button>
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Historial de revisiones
                      </Typography>
                      {!disenoRevisiones.length ? (
                        <Typography variant="body2" color="text.secondary">
                          Aun no hay revisiones registradas.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {disenoRevisiones.map((rev, idx) => (
                            <Accordion
                              key={`rev-diseno-${idx}`}
                              expanded={revisionExpandida === idx}
                              onChange={(_, expanded) => setRevisionExpandida(expanded ? idx : false)}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2" fontWeight={700}>
                                  Revisión diseño {idx + 1} - {rev.decision}
                                  {rev.decision === "Devuelto" && rev.documentos_devueltos?.length
                                    ? ` (${rev.documentos_devueltos
                                        .map((t) => (t === "plano" ? "plano" : "presupuesto"))
                                        .join(", ")})`
                                    : ""}{" "}
                                  - {new Date(rev.fecha).toLocaleString()}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="Decision de revision"
                                      fullWidth
                                      size="small"
                                      value={rev.decision}
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={8}>
                                    <TextField
                                      label="Observacion"
                                      fullWidth
                                      size="small"
                                      multiline
                                      minRows={2}
                                      value={rev.observacion || "Sin observacion."}
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                  {rev.decision === "Devuelto" && !!rev.documentos_devueltos?.length && (
                                    <Grid item xs={12}>
                                      <Alert severity="warning" sx={{ py: 0.5 }}>
                                        Devuelto al solicitante:{" "}
                                        <strong>
                                          {rev.documentos_devueltos
                                            .map((t) => (t === "plano" ? "plano" : "presupuesto"))
                                            .join(" y ")}
                                        </strong>
                                      </Alert>
                                    </Grid>
                                  )}
                                  <Grid item xs={12}>
                                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center" flexWrap="wrap">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!rev.plano?.id || guardando !== null}
                                        onClick={() => abrirDocumentoPorId(Number(rev.plano?.id || 0), TIPO_DOC_PLANO_DISENO)}
                                      >
                                        Ver plano de esta revisión
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!rev.presupuesto?.id || guardando !== null}
                                        onClick={() =>
                                          abrirDocumentoPorId(Number(rev.presupuesto?.id || 0), TIPO_DOC_PRESUPUESTO_DISENO)
                                        }
                                      >
                                        Ver presupuesto de esta revisión
                                      </Button>
                                      <Typography variant="caption" color="text.secondary">
                                        {rev.plano?.id
                                          ? `Plano: ${rev.plano?.nombre || TIPO_DOC_PLANO_DISENO}`
                                          : "Sin plano en esta revisión."}{" "}
                                        {rev.presupuesto?.id
                                          ? `| Presupuesto: ${rev.presupuesto?.nombre || TIPO_DOC_PRESUPUESTO_DISENO}`
                                          : "| Sin presupuesto en esta revisión."}
                                      </Typography>
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                      Parametros registrados en esta revision
                                    </Typography>
                                    <Grid container spacing={1}>
                                      {disenoMatrizNormalizada(rev.matriz).map((filaRev, idxFila) => (
                                        <Grid item xs={12} key={`rev-${idx}-fila-${idxFila}`}>
                                          <Paper variant="outlined" sx={{ p: 1 }}>
                                            <Grid container spacing={1}>
                                              <Grid item xs={12} md={3}>
                                                <TextField
                                                  label={disenoLabels.item}
                                                  fullWidth
                                                  size="small"
                                                  value={idxFila + 1}
                                                  InputProps={{ readOnly: true }}
                                                />
                                              </Grid>
                                              <Grid item xs={12} md={9}>
                                                <TextField
                                                  label={disenoLabels.descripcion_proceso}
                                                  fullWidth
                                                  size="small"
                                                  multiline
                                                  minRows={2}
                                                  value={filaRev.descripcion_proceso || ""}
                                                  InputProps={{ readOnly: true }}
                                                />
                                              </Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Stack>
                      )}
                    </Grid>
                </>
                )}
                {disenoSubTab === DISENO_SUBTAB_LIQUIDACION && (
                  <Grid item xs={12}>
                    <LiquidacionDisenoPanel
                      proyectoId={Number(id)}
                      liquidacionWorkflow={liquidacionDisenoWorkflow}
                      documento={documentosRevisionPorTipo[TIPO_DOC_LIQUIDACION_DISENO]}
                      puedeCargar={puedeGestionarLiquidacionDiseno()}
                      onExito={() => void load()}
                      onVisualizar={() => abrirDocumento(TIPO_DOC_LIQUIDACION_DISENO)}
                      visualizarDisabled={
                        guardando !== null || !documentosRevisionPorTipo[TIPO_DOC_LIQUIDACION_DISENO]?.id
                      }
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {tab === 3 && (
              <React.Fragment>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Carta remisoria y comprobante de pago (jefe unidad PMT)
                      </Typography>
                      <Tabs
                        value={cartaRemisoriaVistaTab}
                        onChange={(_, v) => setCartaRemisoriaVistaTab(v)}
                        sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}
                      >
                        <Tab label="Resumen" />
                        <Tab label="Trazabilidad completa" />
                      </Tabs>
                      {cartaRemisoriaVistaTab === 0 && (
                        <Stack spacing={1}>
                          {liquidacionDisenoEstado === "pendiente_carga" && (
                            <Alert severity="warning">
                              Pendiente de cargar la <strong>liquidación de diseño</strong> (pestaña Diseño →
                              Liquidación). La carta remisoria y el comprobante solo se habilitan en el portal tras
                              enviar la liquidación al solicitante.
                            </Alert>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              Estado:
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                !cartaRemisoriaEstado
                                  ? "Sin requisito activo"
                                  : cartaRemisoriaEstado === "pendiente_carga_solicitante"
                                    ? "Pendiente: carga solicitante (portal)"
                                    : cartaRemisoriaEstado === "pendiente_jefe"
                                      ? "Pendiente: asignacion interventor"
                                      : cartaRemisoriaEstado === "aprobado"
                                        ? "Aprobada"
                                        : cartaRemisoriaEstado === "rechazado"
                                          ? "Rechazada (puede volver a cargar)"
                                          : cartaRemisoriaEstado
                              }
                              color={
                                cartaRemisoriaEstado === "aprobado"
                                  ? "success"
                                  : cartaRemisoriaEstado === "pendiente_jefe"
                                    ? "warning"
                                    : cartaRemisoriaEstado === "rechazado"
                                      ? "error"
                                      : "default"
                              }
                            />
                          </Stack>
                          {Array.isArray(cartaRemisoriaJefe?.historial) && cartaRemisoriaJefe.historial.length > 0 && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              Últimos eventos:{" "}
                              {cartaRemisoriaJefe.historial.slice(-5).map((h: any, i: number) => (
                                <span key={i}>
                                  {i > 0 ? " · " : ""}
                                  {String(h.accion || h.detalle || "")}
                                  {h.fecha ? ` (${new Date(h.fecha).toLocaleString()})` : ""}
                                </span>
                              ))}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Use la pestaña «Trazabilidad completa» para ver fechas, responsables y todo el historial
                            (incluido después de aprobar o rechazar).
                          </Typography>
                        </Stack>
                      )}
                      {cartaRemisoriaVistaTab === 1 && <CartaRemisoriaTrazabilidadVista data={cartaRemisoriaJefe} />}
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Tabs
                      value={supervisionSubTab}
                      onChange={(_, v) => setSupervisionSubTab(v)}
                      sx={{ borderBottom: 1, borderColor: "divider" }}
                    >
                      <Tab label="Supervisión y trazabilidad" />
                      {mostrarTabRetie && <Tab label="Revisión RETIE" />}
                      <Tab label="Grandes clientes" />
                      <Tab label="Informe avance de obra" />
                    </Tabs>
                  </Grid>
                </Grid>
                {supervisionSubTab === 0 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Supervicion y aprobacion: registre la inspección técnica y conserve la trazabilidad de cada supervicion.
                  </Alert>
                </Grid>
                {renderDocumentosEtapa("revision_aprobacion", "Documentos de supervicion y aprobacion", {
                  supervisionRevision: true,
                })}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Estado proceso"
                    fullWidth
                    size="small"
                    value={revisionForm.proceso_estado || "En revision"}
                    InputProps={{ readOnly: true }}
                    helperText="Estado automatico del proceso"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Estructura base de supervisión</Typography>
                  <Grid container spacing={1}>
                    {supervisionMatriz.map((fila, idx) => (
                      <Grid item xs={12} key={`fila-supervision-${idx}`}>
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Grid container spacing={1}>
                            {disenoColumnas.map((col) => (
                              <Grid item xs={12} md={col === "descripcion_proceso" ? 6 : 3} key={`sup-${idx}-${col}`}>
                                <TextField
                                  label={disenoLabels[col]}
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={2}
                                  value={fila[col] || ""}
                                  onChange={(e) =>
                                    setSupervisionMatriz((prev) =>
                                      prev.map((f, i) => (i === idx ? { ...f, [col]: e.target.value } : f)),
                                    )
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1 }}>
                    <Button variant="outlined" onClick={() => setSupervisionMatriz((prev) => [...prev, disenoMatrizFilaVacia()])} disabled={guardando !== null}>Agregar fila base</Button>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8 }}>
                      Acta levantada en terreno (opcional)
                    </Typography>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={4}>
                        {bloquearCargaInternaDoc("revision_doc_acta_levantada_terreno") ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 360 }}>
                            Acta: carga solo por el solicitante (portal público). Administrador puede subir aquí.
                          </Typography>
                        ) : (
                          <CampoArchivoRevision
                            inputId="sup-upload-acta-terreno"
                            tipoDocumento="revision_doc_acta_levantada_terreno"
                            ariaLabel="Acta levantada en terreno"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onArchivo={(file) =>
                              setArchivosSeleccionados((prev) => ({
                                ...prev,
                                revision_doc_acta_levantada_terreno: file,
                              }))
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                          {!bloquearCargaInternaDoc("revision_doc_acta_levantada_terreno") && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => subirDocumentoRevision("revision_doc_acta_levantada_terreno")}
                              disabled={guardando !== null}
                            >
                              Subir acta
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!documentosRevisionPorTipo["revision_doc_acta_levantada_terreno"]?.id || guardando !== null}
                            onClick={() => abrirDocumento("revision_doc_acta_levantada_terreno")}
                          >
                            Visualizar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!documentosRevisionPorTipo["revision_doc_acta_levantada_terreno"]?.id || guardando !== null}
                            onClick={() => descargarDocumento("revision_doc_acta_levantada_terreno")}
                          >
                            Descargar
                          </Button>
                          <Chip
                            size="small"
                            label={documentosRevisionPorTipo["revision_doc_acta_levantada_terreno"]?.id ? "Cargada" : "Sin cargar"}
                            color={documentosRevisionPorTipo["revision_doc_acta_levantada_terreno"]?.id ? "success" : "default"}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      disabled={guardando !== null}
                      onClick={() => setRedisennoModalOpen(true)}
                    >
                      Rediseño (vuelta a fase Diseño)
                    </Button>
                    {redisennoEstado === "pendiente_admin" && (
                      <Chip size="small" color="warning" label="Solicitud pendiente de administrador" />
                    )}
                    {redisennoEstado === "aprobado" && (
                      <Chip size="small" color="success" label="Rediseño autorizado — cargue plano en Diseño" />
                    )}
                    {redisennoEstado === "rechazado" && (
                      <Chip size="small" color="error" label="Solicitud de rediseño rechazada" />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Rediseño — trazabilidad
                    </Typography>
                    <Tabs
                      value={redisennoVistaTab}
                      onChange={(_, v) => setRedisennoVistaTab(v)}
                      sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}
                    >
                      <Tab label="Resumen" />
                      <Tab label="Trazabilidad completa" />
                    </Tabs>
                    {redisennoVistaTab === 0 && (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            Estado:
                          </Typography>
                          {redisennoEstado === "pendiente_admin" && (
                            <Chip size="small" color="warning" label="Pendiente de administrador" />
                          )}
                          {redisennoEstado === "aprobado" && (
                            <Chip size="small" color="success" label="Autorizado — cargar plano en Diseño" />
                          )}
                          {redisennoEstado === "rechazado" && (
                            <Chip size="small" color="error" label="Rechazado" />
                          )}
                          {(redisennoEstado === "ninguno" || !redisennoEstado) && (
                            <Chip size="small" variant="outlined" label="Sin flujo activo" />
                          )}
                        </Stack>
                        {Array.isArray(redisennoWorkflow?.historial) && redisennoWorkflow.historial.length > 0 && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            Últimos eventos:{" "}
                            {[...redisennoWorkflow.historial]
                              .sort(
                                (a: any, b: any) =>
                                  new Date(String(b?.fecha || 0)).getTime() -
                                  new Date(String(a?.fecha || 0)).getTime(),
                              )
                              .slice(0, 5)
                              .map((h: any, i: number) => (
                                <span key={i}>
                                  {i > 0 ? " · " : ""}
                                  {String(h.accion || h.detalle || "")}
                                  {h.fecha ? ` (${new Date(h.fecha).toLocaleString()})` : ""}
                                </span>
                              ))}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Use «Trazabilidad completa» para ver solicitud, resolución de administración y todo el
                          historial (incluido cierre al aprobar diseño).
                        </Typography>
                      </Stack>
                    )}
                    {redisennoVistaTab === 1 && <RedisennoTrazabilidadVista data={redisennoWorkflow || {}} />}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select label="Decision de revision" fullWidth size="small" value={supervisionDecision} onChange={(e) => setSupervisionDecision((e.target.value as "Aprobado" | "Devuelto") || "Aprobado")}>
                    <MenuItem value="Aprobado">Aprobado</MenuItem>
                    <MenuItem value="Devuelto">Devuelto</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField label="Observacion de supervision" fullWidth size="small" multiline minRows={2} value={supervisionObservacion} onChange={(e) => setSupervisionObservacion(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" disabled={guardando !== null} onClick={registrarRevisionSupervision}>Registrar trazabilidad de supervisión</Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Historial de supervisión</Typography>
                  {!supervisionRevisiones.length ? (
                    <Typography variant="body2" color="text.secondary">Aun no hay revisiones registradas.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {supervisionRevisiones.map((rev, idx) => (
                        <Accordion key={`rev-sup-${idx}`} expanded={supervisionExpandida === idx} onChange={(_, expanded) => setSupervisionExpandida(expanded ? idx : false)}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" fontWeight={700}>
                              Supervicion {idx + 1} - {rev.decision} - {new Date(rev.fecha).toLocaleString()}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={4}><TextField label="Decision" fullWidth size="small" value={rev.decision} InputProps={{ readOnly: true }} /></Grid>
                              <Grid item xs={12} md={8}><TextField label="Observacion" fullWidth size="small" multiline minRows={2} value={rev.observacion || "Sin observacion."} InputProps={{ readOnly: true }} /></Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Stack>
                  )}
                </Grid>
              </Grid>
                )}
                {supervisionSubTab === 1 && mostrarTabRetie && (
                  <RetieRevisionPanel
                    interventorAsignacion={interventorAsignacion}
                    retieWorkflow={retieWorkflow}
                    documentosPorTipo={documentosRevisionPorTipo}
                    guardando={guardando}
                    getValidacion={getValidacionRevisionDoc}
                    onRevisar={abrirValidadorDocumento}
                    onVisualizar={abrirDocumento}
                    onDescargar={descargarDocumento}
                  />
                )}
                {supervisionSubTab === supervisionSubtabGrandesClientes && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Flujo de grandes clientes: 1) cargar y revisar solicitud, 2) solicitar por correo al
                        solicitante certificado de tradición y declaración de cumplimiento de deudas, 3) registrar
                        aprobación provisional y cargar documento de validación provisional. El solicitante ve el
                        avance por pasos en{" "}
                        <strong>{`${typeof window !== "undefined" ? window.location.origin : ""}/proyectos/crear/correccion`}</strong>
                        ; allí solo puede cargar lo autorizado y lo ya enviado queda bloqueado.
                      </Alert>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                          Estado actual: {gcEstado || "sin_solicitud"}
                        </Typography>
                        <Grid container spacing={1.2}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              component="label"
                              htmlFor="gc-file-solicitud-grandes-clientes"
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              Solicitud de grandes clientes
                            </Typography>
                            <Box sx={{ mt: 0.5, mb: 1 }}>
                              {bloquearCargaInternaDoc("revision_doc_solicitud_grandes_clientes") ? (
                                <Typography variant="caption" color="text.secondary">
                                  Este documento lo carga el solicitante por portal público. Como administrador también
                                  puede subirlo aquí.
                                </Typography>
                              ) : (
                                <CampoArchivoRevision
                                  inputId="gc-file-solicitud-grandes-clientes"
                                  tipoDocumento="revision_doc_solicitud_grandes_clientes"
                                  ariaLabel="Solicitud de grandes clientes"
                                  onArchivo={(file) =>
                                    setArchivosSeleccionados((prev) => ({
                                      ...prev,
                                      revision_doc_solicitud_grandes_clientes: file,
                                    }))
                                  }
                                />
                              )}
                            </Box>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 0.5 }}>
                              {!bloquearCargaInternaDoc("revision_doc_solicitud_grandes_clientes") && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() =>
                                    subirDocumentoRevision(
                                      "revision_doc_solicitud_grandes_clientes",
                                    )
                                  }
                                  disabled={guardando !== null}
                                >
                                  Subir solicitud
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!documentosRevisionPorTipo["revision_doc_solicitud_grandes_clientes"]?.id || guardando !== null}
                                onClick={() => abrirValidadorDocumento("revision_doc_solicitud_grandes_clientes")}
                              >
                                Validar solicitud
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!documentosRevisionPorTipo["revision_doc_solicitud_grandes_clientes"]?.id || guardando !== null}
                                onClick={() => abrirDocumento("revision_doc_solicitud_grandes_clientes")}
                              >
                                Ver solicitud
                              </Button>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              id="gc-decision-solicitud"
                              name="gc_decision_solicitud"
                              select
                              label="Decisión solicitud grandes clientes"
                              fullWidth
                              size="small"
                              value={gcDecisionSolicitud}
                              onChange={(e) =>
                                setGcDecisionSolicitud((e.target.value as "Aprobado" | "Devuelto") || "Aprobado")
                              }
                              InputLabelProps={{ htmlFor: "gc-decision-solicitud" }}
                              SelectProps={{
                                inputProps: { id: "gc-decision-solicitud", name: "gc_decision_solicitud" },
                              }}
                            >
                              <MenuItem value="Aprobado">Aprobado</MenuItem>
                              <MenuItem value="Devuelto">Devuelto</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              id="gc-obs-solicitud"
                              name="gc_obs_solicitud"
                              label="Observación solicitud (obligatoria si se devuelve)"
                              fullWidth
                              size="small"
                              multiline
                              minRows={2}
                              value={gcObsSolicitud}
                              onChange={(e) => setGcObsSolicitud(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button variant="contained" disabled={guardando !== null} onClick={registrarSolicitudGrandesClientes}>
                              Registrar decisión solicitud grandes clientes
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                          Documentos del solicitante y aprobación provisional
                        </Typography>
                        {(
                          [
                            {
                              tipo: "revision_doc_certificado_libertad",
                              label: "Certificado de tradición",
                              estadoHabilitado:
                                gcEstado === "pendiente_documentos_solicitante" ||
                                gcEstado === "documentos_recibidos_pendiente_provisional" ||
                                gcEstado === "aprobado_provisional",
                            },
                            {
                              tipo: "revision_doc_declaracion_cumplimiento_deudas",
                              label: "Declaración de cumplimiento de deudas",
                              estadoHabilitado:
                                gcEstado === "pendiente_documentos_solicitante" ||
                                gcEstado === "documentos_recibidos_pendiente_provisional" ||
                                gcEstado === "aprobado_provisional",
                            },
                            {
                              tipo: "revision_doc_validacion_provisional",
                              label: "Documento validación provisional",
                              estadoHabilitado:
                                gcEstado === "documentos_recibidos_pendiente_provisional" ||
                                gcEstado === "aprobado_provisional",
                            },
                          ] as const
                        ).map((docCfg) => (
                          <Paper
                            key={docCfg.tipo}
                            variant="outlined"
                            sx={{ p: 1, mb: 1, backgroundColor: "#fafafa" }}
                          >
                            <Typography
                              component="label"
                              htmlFor={`gc-file-${docCfg.tipo}`}
                              variant="body2"
                              fontWeight={700}
                              sx={{ mb: 0.5, display: "block" }}
                            >
                              {docCfg.label}
                            </Typography>
                            <Box sx={{ mb: 0.8 }}>
                              {bloquearCargaInternaDoc(docCfg.tipo) ? (
                                <Typography variant="caption" color="text.secondary">
                                  Este documento lo carga el solicitante por portal público.
                                </Typography>
                              ) : (
                                <CampoArchivoRevision
                                  inputId={`gc-file-${docCfg.tipo}`}
                                  tipoDocumento={docCfg.tipo}
                                  ariaLabel={docCfg.label}
                                  onArchivo={(file) =>
                                    setArchivosSeleccionados((prev) => ({
                                      ...prev,
                                      [docCfg.tipo]: file,
                                    }))
                                  }
                                />
                              )}
                            </Box>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                              {!bloquearCargaInternaDoc(docCfg.tipo) && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => subirDocumentoRevision(docCfg.tipo)}
                                  disabled={guardando !== null || !docCfg.estadoHabilitado}
                                >
                                  Subir
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!documentosRevisionPorTipo[docCfg.tipo]?.id || guardando !== null}
                                onClick={() => abrirDocumento(docCfg.tipo)}
                              >
                                Ver
                              </Button>
                              <Chip
                                size="small"
                                label={documentosRevisionPorTipo[docCfg.tipo]?.id ? "Cargado" : "Sin cargar"}
                                color={documentosRevisionPorTipo[docCfg.tipo]?.id ? "success" : "default"}
                              />
                            </Stack>
                          </Paper>
                        ))}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Certificado de tradición cargado: {documentosRevisionPorTipo["revision_doc_certificado_libertad"]?.id ? "Sí" : "No"} ·
                          Declaración de cumplimiento de deudas cargada: {documentosRevisionPorTipo["revision_doc_declaracion_cumplimiento_deudas"]?.id ? "Sí" : "No"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Documento validación provisional cargado: {documentosRevisionPorTipo["revision_doc_validacion_provisional"]?.id ? "Sí" : "No"}
                        </Typography>
                        <TextField
                          id="gc-obs-provisional"
                          name="gc_obs_provisional"
                          label="Observación aprobación provisional"
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          value={gcObsProvisional}
                          onChange={(e) => setGcObsProvisional(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <Button
                          variant="contained"
                          color="success"
                          disabled={guardando !== null}
                          onClick={registrarAprobacionProvisionalGrandesClientes}
                        >
                          Registrar aprobación provisional
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
                {supervisionSubTab === supervisionSubtabInformeAvance && (
                  <Grid item xs={12}>
                    <InformeAvanceObraPanel
                      proyectoId={Number(id)}
                      workflow={informeAvanceObraWorkflow}
                      documentosPorTipo={documentosRevisionPorTipo}
                      puedeGestionar={puedeGestionarInformeAvanceObra()}
                      onExito={() => void load()}
                      onVisualizar={abrirDocumento}
                      guardando={guardando !== null}
                    />
                  </Grid>
                )}
              </React.Fragment>
            )}

            {tab === 4 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Ejecución de obras: registre cada inspección de obra y mantenga su trazabilidad de decisiones.
                  </Alert>
                </Grid>
                {renderDocumentosEtapa("ejecucion_obras", "Documentos de ejecución de obras")}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Estado proceso"
                    fullWidth
                    size="small"
                    value={ejecucionForm.proceso_estado || "En revision"}
                    InputProps={{ readOnly: true }}
                    helperText="Estado automatico del proceso"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Estructura base de ejecución de obras</Typography>
                  <Grid container spacing={1}>
                    {ejecucionMatriz.map((fila, idx) => (
                      <Grid item xs={12} key={`fila-ejecucion-${idx}`}>
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Grid container spacing={1}>
                            {disenoColumnas.map((col) => (
                              <Grid item xs={12} md={col === "descripcion_proceso" ? 6 : 3} key={`eje-${idx}-${col}`}>
                                <TextField
                                  label={disenoLabels[col]}
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={2}
                                  value={fila[col] || ""}
                                  onChange={(e) =>
                                    setEjecucionMatriz((prev) =>
                                      prev.map((f, i) => (i === idx ? { ...f, [col]: e.target.value } : f)),
                                    )
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1 }}>
                    <Button variant="outlined" onClick={() => setEjecucionMatriz((prev) => [...prev, disenoMatrizFilaVacia()])} disabled={guardando !== null}>Agregar fila base</Button>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8 }}>
                      Soporte de ejecución de obras (opcional)
                    </Typography>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={4}>
                        {bloquearCargaInternaDoc("ejecucion_doc_acta_visita") ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 360 }}>
                            Soporte acta: carga solo por el solicitante (portal público). Administrador puede subir aquí.
                          </Typography>
                        ) : (
                          <CampoArchivoRevision
                            inputId="eje-upload-acta-visita"
                            tipoDocumento="ejecucion_doc_acta_visita"
                            ariaLabel="Acta de visita de avance de obra"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onArchivo={(file) =>
                              setArchivosSeleccionados((prev) => ({
                                ...prev,
                                ejecucion_doc_acta_visita: file,
                              }))
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                          {!bloquearCargaInternaDoc("ejecucion_doc_acta_visita") && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => subirDocumentoRevision("ejecucion_doc_acta_visita")}
                              disabled={guardando !== null}
                            >
                              Subir archivo
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!documentosRevisionPorTipo["ejecucion_doc_acta_visita"]?.id || guardando !== null}
                            onClick={() => abrirDocumento("ejecucion_doc_acta_visita")}
                          >
                            Visualizar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!documentosRevisionPorTipo["ejecucion_doc_acta_visita"]?.id || guardando !== null}
                            onClick={() => descargarDocumento("ejecucion_doc_acta_visita")}
                          >
                            Descargar
                          </Button>
                          <Chip
                            size="small"
                            label={documentosRevisionPorTipo["ejecucion_doc_acta_visita"]?.id ? "Cargado" : "Sin cargar"}
                            color={documentosRevisionPorTipo["ejecucion_doc_acta_visita"]?.id ? "success" : "default"}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select label="Decision de revision" fullWidth size="small" value={ejecucionDecision} onChange={(e) => setEjecucionDecision((e.target.value as "Aprobado" | "Devuelto") || "Aprobado")}>
                    <MenuItem value="Aprobado">Aprobado</MenuItem>
                    <MenuItem value="Devuelto">Devuelto</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField label="Observacion de ejecución de obras" fullWidth size="small" multiline minRows={2} value={ejecucionObservacion} onChange={(e) => setEjecucionObservacion(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" disabled={guardando !== null} onClick={registrarRevisionEjecucion}>Registrar trazabilidad de ejecución</Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Historial de ejecución de obras</Typography>
                  {!ejecucionRevisiones.length ? (
                    <Typography variant="body2" color="text.secondary">Aun no hay revisiones registradas.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {ejecucionRevisiones.map((rev, idx) => (
                        <Accordion key={`rev-eje-${idx}`} expanded={ejecucionExpandida === idx} onChange={(_, expanded) => setEjecucionExpandida(expanded ? idx : false)}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" fontWeight={700}>
                              Supervicion {idx + 1} - {rev.decision} - {new Date(rev.fecha).toLocaleString()}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={4}><TextField label="Decision" fullWidth size="small" value={rev.decision} InputProps={{ readOnly: true }} /></Grid>
                              <Grid item xs={12} md={8}><TextField label="Observacion" fullWidth size="small" multiline minRows={2} value={rev.observacion || "Sin observacion."} InputProps={{ readOnly: true }} /></Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Stack>
                  )}
                </Grid>
              </Grid>
            )}

            {tab === 5 && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Tabs
                    value={puestaSubtab}
                    onChange={(_, v) => setPuestaSubtab(v)}
                    sx={{ mb: 1, minHeight: 40 }}
                  >
                    <Tab label="Energizacion" />
                    <Tab label="Calculo de burden" />
                  </Tabs>
                </Grid>

                {puestaSubtab === 1 && (
                  <Grid item xs={12}>
                    <CalculoBurdenPanel
                      proyectoId={Number(id) || null}
                      proyectoCodigo={codigoSolicitud || detalle?.proyecto?.codigo}
                      proyectoNombre={detalle?.proyecto?.nombre}
                      nivelTension={
                        fichaSolicitud?.nivel_tension || detalle?.proyecto?.nivel_tension || undefined
                      }
                      distanciaRedM={(fichaSolicitud as any)?.distancia_red}
                    />
                  </Grid>
                )}

                {puestaSubtab === 0 && (
                  <>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#f8fafc" }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Avance de puesta en servicio
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={800}>
                        {avancePuesta}%
                      </Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={avancePuesta} />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}><TextField label="Tipo de solicitud" fullWidth size="small" value={puestaForm.tipo_solicitud || ""} onChange={(e) => setPuestaForm({ ...puestaForm, tipo_solicitud: e.target.value })} /></Grid>
                <Grid item xs={12} md={9}><TextField label="Nombre" fullWidth size="small" value={puestaForm.nombre || ""} onChange={(e) => setPuestaForm({ ...puestaForm, nombre: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Fecha de maniobra" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.fecha_maniobra || ""} onChange={(e) => setPuestaForm({ ...puestaForm, fecha_maniobra: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Fin estimada" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.fecha_fin_estimada || ""} onChange={(e) => setPuestaForm({ ...puestaForm, fecha_fin_estimada: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Recepcion correo" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.fecha_recepcion_correo || ""} onChange={(e) => setPuestaForm({ ...puestaForm, fecha_recepcion_correo: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Recepcion fisica" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.fecha_recepcion_fisica || ""} onChange={(e) => setPuestaForm({ ...puestaForm, fecha_recepcion_fisica: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Maniobra comite" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.fecha_maniobra_comite || ""} onChange={(e) => setPuestaForm({ ...puestaForm, fecha_maniobra_comite: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Maniobra final fecha" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.maniobra_final_fecha || ""} onChange={(e) => setPuestaForm({ ...puestaForm, maniobra_final_fecha: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Dias entrega fisica vs comite" type="number" fullWidth size="small" value={puestaForm.dias_entrega_fisica_vs_comite || ""} onChange={(e) => setPuestaForm({ ...puestaForm, dias_entrega_fisica_vs_comite: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Macromedicion" fullWidth size="small" value={puestaForm.macromedicion || ""} onChange={(e) => setPuestaForm({ ...puestaForm, macromedicion: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Fecha instalacion macromedicion" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={puestaForm.fecha_instalacion_macromedicion || ""} onChange={(e) => setPuestaForm({ ...puestaForm, fecha_instalacion_macromedicion: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Contrato macromedicion" fullWidth size="small" value={puestaForm.contrato_macromedicion || ""} onChange={(e) => setPuestaForm({ ...puestaForm, contrato_macromedicion: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Estado" fullWidth size="small" value={puestaForm.estado || ""} onChange={(e) => setPuestaForm({ ...puestaForm, estado: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Proceso estado" fullWidth size="small" value={puestaForm.proceso_estado || ""} onChange={(e) => setPuestaForm({ ...puestaForm, proceso_estado: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Alerta" fullWidth size="small" value={puestaForm.alerta || ""} onChange={(e) => setPuestaForm({ ...puestaForm, alerta: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField label="% avance" fullWidth size="small" type="number" value={avancePuesta} InputProps={{ readOnly: true }} /></Grid>
                <Grid item xs={12}><TextField label="Link de carpeta" fullWidth size="small" value={puestaForm.link_carpeta || ""} onChange={(e) => setPuestaForm({ ...puestaForm, link_carpeta: e.target.value })} /></Grid>
                <Grid item xs={12}><Typography variant="subtitle2" fontWeight={700}>Indicadores operativos (0 a 100)</Typography></Grid>
                {puestaMetricas.map((k) => (
                  <Grid item xs={12} md={3} key={k}>
                    <TextField
                      label={k.replace(/_/g, " ")}
                      type="number"
                      size="small"
                      fullWidth
                      value={puestaForm[k] ?? 0}
                      onChange={(e) => setPuestaForm({ ...puestaForm, [k]: Number(e.target.value || 0) })}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}><TextField label="Observacion" fullWidth size="small" multiline minRows={4} value={puestaForm.observacion || ""} onChange={(e) => setPuestaForm({ ...puestaForm, observacion: e.target.value })} /></Grid>
                <Grid item xs={12}><Button variant="contained" disabled={guardando !== null} onClick={() => patchData(`/${id}/puesta-servicio`, { ...puestaForm, avance_porcentaje: avancePuesta }, "Puesta en servicio guardada.")}>Guardar puesta en servicio</Button></Grid>
                  </>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={redisennoModalOpen} onClose={() => setRedisennoModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rediseño (vuelta a fase Diseño)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use esta opción si, a partir de la estructura de supervisión o del acta de terreno, debe volverse al diseño
            inicial. El administrador debe aprobar la solicitud en el menú lateral «Aprobaciones rediseño»; hasta entonces
            la pestaña Diseño queda bloqueada.
          </Typography>
          {redisennoEstado === "pendiente_admin" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Pendiente de administrador.</strong> Motivo registrado: {String(redisennoWorkflow.motivo || "—")}
            </Alert>
          )}
          {redisennoEstado === "aprobado" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Autorizado: el equipo puede cargar de nuevo el plano en la pestaña Diseño.
            </Alert>
          )}
          {redisennoEstado === "rechazado" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Solicitud rechazada
              {redisennoWorkflow.comentario_admin
                ? `: ${String(redisennoWorkflow.comentario_admin)}`
                : ". Puede enviar una nueva solicitud si aplica."}
            </Alert>
          )}
          {(redisennoEstado === "ninguno" || redisennoEstado === "rechazado") && (
            <TextField
              label="Observación: por qué se requiere rediseñar"
              fullWidth
              size="small"
              multiline
              minRows={4}
              value={redisennoMotivo}
              onChange={(e) => setRedisennoMotivo(e.target.value)}
              placeholder="Ej.: Cambios en condiciones de red, observaciones del acta de terreno, etc."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRedisennoModalOpen(false)}>Cerrar</Button>
          {(redisennoEstado === "ninguno" || redisennoEstado === "rechazado") && (
            <Button variant="contained" color="warning" disabled={guardando !== null} onClick={solicitarRedisenno}>
              Enviar solicitud al administrador
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={docModalOpen} onClose={() => setDocModalOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{docModalSoloLectura ? "Visualizador documental" : "Validador documental"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={8}>
              {docModalUrl ? (
                <Box component="iframe" src={docModalUrl} title="Documento" sx={{ width: "100%", height: 500, border: 0 }} />
              ) : (
                <Typography color="text.secondary">No fue posible visualizar el documento.</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.2}>
                <TextField label="Tipo documento" value={docModalTipo} size="small" InputProps={{ readOnly: true }} />
                {!docModalSoloLectura ? (
                  <>
                    <TextField
                      select
                      label="Decision"
                      value={docModalEstado}
                      size="small"
                      onChange={(e) => setDocModalEstado((e.target.value as "Aprobado" | "Desaprobado") || "Aprobado")}
                    >
                      <MenuItem value="Aprobado">Aprobado</MenuItem>
                      <MenuItem value="Desaprobado">Desaprobado</MenuItem>
                    </TextField>
                    <TextField
                      label="Observacion"
                      value={docModalObservacion}
                      onChange={(e) => setDocModalObservacion(e.target.value)}
                      multiline
                      minRows={4}
                      required={docModalEstado === "Desaprobado"}
                      helperText={
                        docModalEstado === "Desaprobado"
                          ? "Obligatoria al desaprobar. Se envía al solicitante si devuelve la documentación."
                          : ""
                      }
                    />
                  </>
                ) : (
                  <>
                    <TextField label="Estado" value={docModalEstado} size="small" InputProps={{ readOnly: true }} />
                    <TextField
                      label="Observacion"
                      value={docModalObservacion || "-"}
                      multiline
                      minRows={4}
                      InputProps={{ readOnly: true }}
                    />
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocModalOpen(false)}>Cerrar</Button>
          {!docModalSoloLectura && (
            <Button variant="contained" onClick={guardarValidacionDocumento} disabled={guardando !== null}>
              Guardar validacion
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
